import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { KeyValue, Location } from '@angular/common';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { FormFieldDialogComponent } from '../dialogs/form-field-dialog/form-field-dialog.component';
import { LogicService } from 'src/app/services/logic.service';
import { environment } from 'src/environments/environment';
import { TextControlService } from 'src/app/services/text-control.service';
import { DocumentService } from 'src/app/services/document.service';
import { DocumentModel, EditorMetaModel } from 'src/app/models/document.model';
import { AnswersChangedDialogComponent } from '../dialogs/answers-changed-dialog/answers-changed-dialog.component';
import { OnlineClientService } from 'src/app/services/online-client.service';
import { FileMenuDialogComponent } from '../dialogs/file-menu-dialog/file-menu-dialog.component';
import {
  DocumentActionClose,
  DocumentActionDownload,
  DocumentActionDownloadAsPdf,
  DocumentActionLogout,
  DocumentActionModel,
  DocumentActionNew,
  DocumentActionOpen,
  DocumentActionOpenNewTab,
  DocumentActionPrint,
  DocumentActionSave,
  DocumentActionSaveAs,
  DocumentActionUpload,
} from 'src/app/models/document-action.model';
import { ToastService } from 'src/app/services/toast.service';
import { AnswerModel } from 'src/app/models/answer.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { UnsavedChangesDialogComponent } from '../dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component';
import {
  catchError,
  combineLatest,
  concatMap,
  debounce,
  finalize,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  throttle,
  timer,
  toArray,
} from 'rxjs';
import { CaseModel } from 'src/app/models/case.model';
import { FileMenuActions } from 'src/app/models/file-menu.model';
import { fileMenuItem, fileTab, txdocsMenuItem, txdocsTab } from './edit-document.helpers';
import { AutosaveService } from 'src/app/services/autosave.service';
import { AutosaveModel } from 'src/app/models/autosave.model';
import { RecoverDocumentsDialogComponent } from '../dialogs/recover-documents-dialog/recover-documents-dialog.component';
import { AppInsightsService } from 'src/app/services/app-insights.service';
import {
  FormField,
  InputPositionChangedEvent,
  SelectionPosition,
  TextFieldEventArgs,
} from '../../services/text-control.service.interface';
import { LoadingAction } from 'src/app/models/loading-action.model';
import { ObjectModel } from 'src/app/models/object.model';
import { FolderService } from 'src/app/services/folder.service';

import { DocumentEditorComponent } from '@txtextcontrol/tx-ng-document-editor';
import { DebugDialogComponent } from '../dialogs/debug-dialog/debug-dialog.component';
import { DialogService } from 'src/app/services/dialog.service';
import { ContactService } from 'src/app/services/contact.service';
import { ContactAddressModel, ContactModel } from 'src/app/models/contact.model';
import { WindowService } from 'src/app/services/window.service';
import { HeaderComponent } from '../header/header.component';
import { ANSWERS_LOCKED } from 'src/app/constants/document.constants';
import { AnswerLockModel } from 'src/app/models/answer-lock.model';
import { PdfSavedDialogComponent } from '../dialogs/pdf-saved-dialog/pdf-saved-dialog.component';
import { SessionService } from 'src/app/services/session.service';
import { AnswerLockService } from 'src/app/services/answer-lock.service';
import { LoadingService } from 'src/app/services/loading.service';
import { FindAndReplaceDialogComponent } from '../dialogs/find-and-replace-dialog/find-and-replace-dialog.component';
import { WebsocketClosedDialogComponent } from '../dialogs/websocket-closed-dialog/websocket-closed-dialog.component';
import { IntroductionDialogComponent } from '../dialogs/introduction-dialog/introduction-dialog.component';
import { PreferencesService } from 'src/app/services/preferences.service';
import { SnippetAddDialogComponent } from '../dialogs/snippet-add-dialog/snippet-add-dialog.component';
import { TextControlContext } from 'textcontrol-promises-develop';
import { TextChar } from 'custom_modules/textcontrol-promises-develop/src/TextChar';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { DictionaryModel } from 'src/app/models/dictionary.model';
import { SharedMessageService } from 'src/app/services/shared-message.service';
import { SharedMessageModel, SharedMessageType } from 'src/app/models/shared-message.model';
import { PendingTakeoverSessionsDialogComponent } from '../dialogs/pending-takeover-sessions-dialog/pending-takeover-sessions-dialog.component';
import { ClipboardService } from 'src/app/services/clipboard.service';
import { PermissionStatus } from 'src/app/models/permissions.model';
import { RibbonClipboardOverlayComponent } from '../ribbon-clipboard-overlay/ribbon-clipboard-overlay.component';
import { RibbonClipboardPasteComponent } from '../ribbon-clipboard-paste/ribbon-clipboard-paste.component';
import * as UAParser from 'ua-parser-js';
import { PasteDialogComponent } from '../dialogs/paste-dialog/paste-dialog.component';
import { smartenQuotes } from 'src/app/helpers/smart-quote-helper';
import { LogicResponseModel, LogicResultModel } from 'src/app/models/logic.model';
declare const TXTextControl: any;
declare const globalThis: any;

@Component({
  selector: 'tx-edit-document',
  templateUrl: './edit-document.component.html',
  styleUrls: ['./edit-document.component.scss'],
  providers: [OnlineClientService],
})
export class EditDocumentComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  public position$ = new Observable<any>();
  public icodeChangedSubscription$: Subscription | undefined = undefined;
  public autosaveChangedSubscription$: Subscription | undefined = undefined;
  public refreshLoadingEventSubscription$: Subscription | undefined = undefined;

  public websocketUrl: string;
  public websocketOpened: boolean;
  public isNew: boolean;
  public fileName?: string;
  public firmId?: string;
  public firmName?: string;
  public document: DocumentModel;
  public isAnswersDirty: boolean;
  public isDocumentDirty: boolean;
  public currentInputValue?: string;
  public onblurHandler: any;
  public onfocusHandler: any;
  public onPageHideHandler: any;
  public onKeyDownHandler: any;
  public onKeyUpHandler: any;
  public initials: string;
  private download: string;
  public showUnansweredOnly: boolean;
  private answerSearch: string;
  public unansweredCount: number;
  private onTextViewLocationChangedHandler: any;
  public showSideBarCloseButton = false;
  public disableSideBarCloseButton = false;
  public isLoading: boolean = false;
  private textControlRetryCount: number = 0;

  public isRecovered = false;
  public showTxDocsRibbonBar = false;
  private documentEventLogName = 'DocumentAction';
  private previousTimestamp: number = Date.now();
  private formFieldToUndo: string = '';
  public formFieldToDelete: string = '';
  public updatingAnswersFromPool: boolean = false;
  public updatingLogic: boolean = false;
  public revertingAnswer: boolean = false;
  private headerFooterActive: boolean = false;
  private defaultFilename: string = 'Untitled Document';
  private txcontrolNavigationInProcess: boolean = false;
  public isActiveFieldSelected = false;
  public isFormFieldDocked: boolean = false;
  public isFileMenuOpen: boolean = false;
  public refreshSessionId: string = '';
  public refreshDcmId: string = '';
  private fieldsToRemove: number[] = [];
  private selection?: SelectionPosition;
  private InputPosition?: InputPositionChangedEvent;
  private textSelected: boolean = false;
  public replaceCalledSubscription$: Subscription | undefined;
  private hasPendingTakeoverSessions: boolean = false;
  private hasDisplayedClipboardMessage = false;
  private txTextControlLoaded: boolean = false;
  pasteDocumentChangedEventHandler: any;

  @HostBinding('class.fieldNavigatorSidebarVisible') fieldNavigatorSidebarVisible: boolean = false;

  @ViewChild(FormFieldDialogComponent) formFieldModal: FormFieldDialogComponent | undefined;
  @ViewChild('txTextControlComponent') txTextControlComponent!: DocumentEditorComponent;
  @ViewChild('clipboardOverlay') ribbonClipboardOverlay!: RibbonClipboardOverlayComponent;
  @ViewChild('clipboardPaste') ribbonClipboardPasteComponent!: RibbonClipboardPasteComponent;
  @ViewChild(HeaderComponent) headerComponent!: HeaderComponent;
  clipboardOpacity: any;
  @HostListener('document:txDocumentEditorLoaded', ['$event'])
  onTxDocumentEditorLoaded() {
    const navigationEntries: any = performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0) {
      const navigationEntry = navigationEntries[0];
      if (navigationEntry.type === 'reload') {
        console.log('==>Refreshed the BROWSER');
        this.refreshSessionId = sessionStorage.getItem('refresh_sessionId') ?? '';
        this.refreshDcmId = sessionStorage.getItem('refresh_dcmId') ?? '';
        console.log('==> refreshSessionId', this.refreshSessionId);
        console.log('==> refreshDcmId', this.refreshDcmId);
      } else {
        console.log('==> NO REFRESH');
      }
      sessionStorage.removeItem('refresh_sessionId');
      sessionStorage.removeItem('refresh_dcmId');
    }
    this.textControlService.TxDocumentEditorLoaded(this.txTextControlComponent.settings.ConnectionID);
    this.document.txTextSessionID = this.textControlService.txTextSessionID;
    this.textControlService.registerEventListener('textControlLoaded', this.onTextControlLoaded.bind(this));
    this.position$ = this.textControlService.position$;
  }
  @HostListener('loaderUpdated', ['$event'])
  onLoaderUpdated(event: any) {
    this.changeDetectorRef.detectChanges();
  }
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.document.dcmID && !this.isRecovered) {
      sessionStorage.setItem('refresh_dcmId', this.document.dcmID);
    }
    if ((this.isAnswersDirty || this.isDocumentDirty) && this.websocketOpened) {
      this.autosaveService.forceAutosaveBeacon(
        this.document,
        this.textControlService.txTextSessionID,
        sessionStorage.getItem('recover_autosave') ?? ''
      );
      const confirmationMessage = 'There are unsaved changes.  Are you sure you want to leave this page?';
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    } else if (this.hasPendingTakeoverSessions) {
      const confirmationMessage =
        'There are documents from the same case waiting to be taken over. If you don\t take them over you will lose any changes. Are you sure you want to leave this page?';
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    }
    return undefined;
  }

  constructor(
    private elRef: ElementRef,
    private logicService: LogicService,
    private textControlService: TextControlService,
    private documentService: DocumentService,
    private documentDataService: DocumentDataService,
    private onlineClientService: OnlineClientService,
    private toastService: ToastService,
    private authenticationService: AuthenticationService,
    private logger: AppInsightsService,
    private offCanvasService: NgbOffcanvas,
    private autosaveService: AutosaveService,
    private folderService: FolderService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private location: Location,
    private contactService: ContactService,
    private windowService: WindowService,
    private sessionService: SessionService,
    private answerLockService: AnswerLockService,
    private renderer: Renderer2,
    public loadingService: LoadingService,
    private ngZone: NgZone,
    private preferencesService: PreferencesService,
    private dictionaryService: DictionaryService,
    private sharedMessage: SharedMessageService,
    private clipboardService: ClipboardService
  ) {
    this.websocketUrl = environment.endpoints.websocketUrl;
    this.websocketOpened = false;
    this.isNew = true;
    this.fileName = 'Untitled Document';
    this.document = {
      isActiveDocument: false,
      saveAsPdf: false,
      answersChanged: false,
      txTextSessionID: this.textControlService.txTextSessionID,
      openInAnotherSession: false,
      pdfAnswersChanged: false,
      txdocsAnswersChanged: false,
    };
    this.isAnswersDirty = false;
    this.isDocumentDirty = false;
    this.download = '';
    this.onfocusHandler = this.onFocus.bind(this);
    this.onPageHideHandler = this.onPageHide.bind(this);
    this.onblurHandler = this.onblur.bind(this);
    this.onKeyDownHandler = this.onKeydown.bind(this);
    this.onKeyUpHandler = this.onKeyup.bind(this);
    this.pasteDocumentChangedEventHandler = this.onPasteDocumentChangedEventHandler.bind(this);
    this.showUnansweredOnly = false;
    this.answerSearch = '';
    this.unansweredCount = 0;
    this.initials = authenticationService.getInitials();
    this.onTextViewLocationChangedHandler = this.onTextViewLocationChanged.bind(this);
    this.isLoading = true;
    this.refreshLoadingEventSubscription$ = this.loadingService.events$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.changeDetectorRef.detectChanges();
      });
    this.replaceCalledSubscription$ = this.textControlService.replaceCalled$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((item: FormField | undefined) => {
        if (item) {
          this.onFormFieldValueChanged(item.details.name, item.details.text);
        }
      });

    this.subscribeToSharedMessage();
  }

  OnInit(): void {
    this.addWindowEventListeners();
    this.sharedMessage.post('Hello world');
  }

  private addTXTextControlEventListeners() {
    this.textControlService.registerEventListener('textViewLocationChanged', this.onTextViewLocationChangedHandler);
    this.textControlService.registerEventListener('ribbonTabsLoaded', this.initSideBarCustomization.bind(this));
    this.textControlService.registerEventListener('ribbonTabIndexChanged', this.onTabIndexChangedHandler.bind(this));

    this.textControlService.registerEventListener('textFieldEntered', this.onFieldEntered.bind(this));
    this.textControlService.registerEventListener('textFieldLeft', this.onFieldLeft.bind(this));
    this.textControlService.registerEventListener('formFieldTextChanged', this.onFormFieldChanged.bind(this));
    this.textControlService.registerEventListener('formFieldDateChanged', this.onFormFieldChanged.bind(this));
    this.textControlService.registerEventListener('formFieldSelectionChanged', this.onFormFieldChanged.bind(this));
    this.textControlService.registerEventListener('fileDropped', this.onFileDropped.bind(this));
    this.textControlService.registerEventListener('webSocketClosed', this.onWebSocketClosed.bind(this));
    this.textControlService.registerEventListener('contextMenuOpening', this.onBuildCustomContextMenu.bind(this));
  }

  private removeTXTextControlEventListeners() {
    this.textControlService.removeEventListener('textControlLoaded', this.onTextControlLoaded.bind(this));
    this.textControlService.removeEventListener('textViewLocationChanged', this.onTextViewLocationChangedHandler);
    this.textControlService.removeEventListener('ribbonTabsLoaded', this.initSideBarCustomization.bind(this));
    this.textControlService.removeEventListener('ribbonTabIndexChanged', this.onTabIndexChangedHandler.bind(this));

    this.textControlService.removeEventListener('textFieldEntered', this.onFieldEntered.bind(this));
    this.textControlService.removeEventListener('textFieldLeft', this.onFieldLeft.bind(this));
    this.textControlService.removeEventListener('formFieldTextChanged', this.onFormFieldChanged.bind(this));
    this.textControlService.removeEventListener('formFieldDateChanged', this.onFormFieldChanged.bind(this));
    this.textControlService.removeEventListener('formFieldSelectionChanged', this.onFormFieldChanged.bind(this));
    this.textControlService.removeEventListener('fileDropped', this.onFileDropped.bind(this));
    this.textControlService.removeEventListener('webSocketClosed', this.onWebSocketClosed.bind(this));
  }

  get documentDisconnectedFromTXdocsAnswerPool(): boolean {
    return this.documentService.documentDisconnectedFromTXdocsAnswerPool;
  }

  ngOnInit(): void {
    document.addEventListener('keydown', this.onKeyDownHandler, true);
    localStorage.setItem('pageloaded', 'yes');
    this.location.replaceState('/', '');
    this.addWindowEventListeners();
    this.checkIfTXtextControlLoaded();
  }
  ngAfterViewInit() {
    this.textControlService.txTextSettings = this.txTextControlComponent.settings;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.removeWindowEventListeners();
    this.removeTXTextControlEventListeners();
    this.dialogService.closeDialogs();
    this.textControlService.removeFromDOM();
    this.isDocumentDirty = false;
    this.isAnswersDirty = false;
  }

  onTextViewLocationChanged(e: any) {
    this.ngZone.run(() => {
      this.checkSideBarType();
      this.changeDetectorRef.detectChanges();
    });
  }

  onTabIndexChangedHandler(e: any) {
    this.ngZone.run(() => {
      this.showTxDocsRibbonBar = e.name === 'tabTxdocs';
      this.changeDetectorRef.detectChanges();
    });
  }

  checkSideBarType() {
    this.fieldNavigatorSidebarVisible = document.getElementById('fieldNavigatorSidebar')?.offsetParent !== null;
    this.changeDetectorRef.detectChanges();
  }

  onBuildCustomContextMenu(baseMenu: TXTextControl.ContextMenuItem) {
    if (!baseMenu || !baseMenu.items) return;
    //  myMenuItem.text = "Custom Context Menu Item. Location: " + menu.location;

    //   menu.items.push(mySeparator);
    //   menu.items.push(myMenuItem);

    const that = this;
    const addToDictionaryIndex = baseMenu.items.findIndex((i) => i.text === 'Add to Dictionary');
    if (addToDictionaryIndex > -1) {
      baseMenu.items[addToDictionaryIndex].clickHandler = function () {
        that.saveFirmDictionary();
      };
    } else {
      const copyMenuItem = baseMenu.items.find((i) => i.text === 'Copy');
      if (copyMenuItem) {
        copyMenuItem.clickHandler = (e: TXTextControl.ContextMenuItemClickEventArgs) => {
          that.onCopyButtonClicked(e);
        };
      }

      const cutMenuItem = baseMenu.items.find((i) => i.text === 'Cut');
      if (cutMenuItem) {
        cutMenuItem.clickHandler = (e: TXTextControl.ContextMenuItemClickEventArgs) => {
          that.onCutButtonClicked(e);
        };
      }

      const pasteMenuItem = baseMenu.items.find((i) => i.text === 'Paste');

      if (pasteMenuItem) {
        pasteMenuItem.clickHandler = (e: TXTextControl.ContextMenuItemClickEventArgs) => {
          that.onPasteButtonClicked(e);
        };
        pasteMenuItem.isEnabled = true;
        pasteMenuItem.items = [];
        const formattedTextPasteMenuItem = pasteMenuItem.items?.find((i) => i.text === 'Formatted Text');
        if (formattedTextPasteMenuItem) {
          formattedTextPasteMenuItem.clickHandler = (e: TXTextControl.ContextMenuItemClickEventArgs) => {
            this.displayClipboardToast();
          };
        }
      }

      let snippetSeparator = {
        __id: 'snippetSeparator',
        isChecked: false,
        isEnabled: true,
        text: '',
        imageUrl: '',
        dropDownIsScrollable: false,
        items: [],
      };
      baseMenu.items.push(snippetSeparator);

      let addSnippetItem = {
        __id: 'addSnippetItem',
        isChecked: false,
        isEnabled: true,
        text: 'Create a New Snippet',
        imageUrl:
          'data:image/svg+xml;base64,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',
        dropDownIsScrollable: false,
        items: [],
        clickHandler: () => {
          this.ngZone.run(() => {
            this.displaySnippetAddDialog();
          });
        },
      };
      baseMenu.items.push(addSnippetItem);

      let snippetMenuItem = {
        __id: 'snippetMenuItem',
        isChecked: false,
        isEnabled: !this.textSelected,
        text: 'Insert a Snippet here',
        imageUrl:
          'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9ImN1cnJlbnRDb2xvciIgdmlld0JveD0iMCAwIDE2IDE2IiBjbGFzcz0iIj48cGF0aCAgZD0iTSAxLjUgMSBDIDEuMjI0IDEgMSAxLjIyNCAxIDEuNSBMIDEgNS41IEMgMSA1Ljg4NSAwLjU4MyA2LjEyNSAwLjI1IDUuOTMzIEMgMC4wOTUgNS44NDQgMCA1LjY3OSAwIDUuNSBMIDAgMS41IEMgMCAwLjY3MiAwLjY3MiAwIDEuNSAwIEwgNS41IDAgQyA1Ljg4NSAwIDYuMTI1IDAuNDE3IDUuOTMzIDAuNzUgQyA1Ljg0NCAwLjkwNSA1LjY3OSAxIDUuNSAxIEwgMS41IDEgWiBNIDE1LjUgMTAgQyAxNS43NzYgMTAgMTYgMTAuMjI0IDE2IDEwLjUgTCAxNiAxNC41IEMgMTYgMTUuMzI4IDE1LjMyOCAxNiAxNC41IDE2IEwgMTAuNSAxNiBDIDEwLjExNSAxNiA5Ljg3NSAxNS41ODMgMTAuMDY3IDE1LjI1IEMgMTAuMTU2IDE1LjA5NSAxMC4zMjEgMTUgMTAuNSAxNSBMIDE0LjUgMTUgQyAxNC43NzYgMTUgMTUgMTQuNzc2IDE1IDE0LjUgTCAxNSAxMC41IEMgMTUgMTAuMjI0IDE1LjIyNCAxMCAxNS41IDEwIiBzdHlsZT0iZmlsbDogcmdiYSg4MCwgODAsIDgwLCAwLjgpOyI+PC9wYXRoPjxnICBpZD0iU25pcHBldExpc3QiIHRyYW5zZm9ybT0ibWF0cml4KDAuNTg3OTM5LCAwLCAwLCAwLjU4NzkzOSwgMC42NzAwMTUsIDEuMDk4ODI1KSI+PHBhdGggIGlkPSJWZWN0b3IiIGQ9Ik00IDE4SDE0TTQgMTRIMjBNNCAxMEgxNE00IDZIMjAiIHN0cm9rZT0iIzAwMDAwMCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHlsZT0ic3Ryb2tlLXdpZHRoOiAxLjcwMDg2cHg7Ij48L3BhdGg+PC9nPjwvc3ZnPg==',
        dropDownIsScrollable: false,
        items: [],
        clickHandler: () => {
          this.ngZone.run(() => {
            this.displaySnippetListDropdown();
          });
        },
      };
      baseMenu.items.push(snippetMenuItem);
      function ClickMe() {
        alert('Custom Click Event');
      }
    }
  }

  private displaySnippetListDropdown() {
    TXTextControl.ribbon.selectedTab = 'tabTxdocs';
    TXTextControl.ribbon.selectedGroup = 'groupSnippets';
    const snippetButton = document.getElementById('ribbon-txdocs-snippet-list');
    if (snippetButton) {
      snippetButton.click();
      this.changeDetectorRef;
    }
  }

  private displaySnippetAddDialog() {
    const modalRef = this.dialogService.openCustomDialog('addSnippet', SnippetAddDialogComponent, {
      size: 'md',
      backdrop: 'static',
    });
    this.changeDetectorRef.detectChanges();
    modalRef.result.then(
      (res) => {
        TXTextControl.focus();
      },
      (res) => {
        TXTextControl.focus();
      }
    );
  }

  openFileMenu() {
    this.dialogService.fileMenuRef = this.offCanvasService.open(FileMenuDialogComponent, {
      panelClass: 'file-menu-panel',
      backdropClass: 'file-menu-backdrop',
    });
    this.logger.logEvent(this.documentEventLogName, {
      method: 'openFileMenu',
      offCanvasRef: this.dialogService.fileMenuRef,
    });
    return this.dialogService.fileMenuRef;
  }

  onDockedChanged(docked: boolean) {
    this.isFormFieldDocked = docked;
    this.changeDetectorRef.detectChanges();
  }

  public onFileClicked(preselectedComponent: string) {
    if (this.isFileMenuOpen || this.loadingService.loading) return;
    this.isFileMenuOpen = true;
    // TODO apply field value before opening
    console.debug('START onFileClicked Calling TextControlActiveField');

    const ref = this.openFileMenu();
    ref.componentInstance.isDirty = this.isDocumentDirty || this.isAnswersDirty;
    ref.componentInstance.document = this.document;
    ref.componentInstance.preselectedComponent = preselectedComponent;

    this.document.txTextSessionID = this.textControlService.txTextSessionID;
    ref.result
      .then((res: DocumentActionModel) => {
        if (res && res.action) {
          switch (res.action) {
            case DocumentActionNew:
              //Redirect URL is retrieved from: Document/NewDocument, new-document-component has the logic.
              break;
            case DocumentActionSave:
              if (this.isRecovered) {
                this.onFileClicked(FileMenuActions.SaveAs);
              } else {
                this.save(this.document);
              }
              break;
            case DocumentActionOpen:
              res.document!.txTextSessionID = this.textControlService.txTextSessionID;
              this.openDocument(res.document);
              break;
            //REMOVED 20240418 --Documents opened in New tab follows different logical path from hidden form on client-case-documents.component
            // case DocumentActionOpenNewTab:
            //   res.document!.txTextSessionID = this.textControlService.txTextSessionID;
            //   this.openDocument(res.document);
            //   break;
            case DocumentActionSaveAs:
              res.document!.txTextSessionID = this.textControlService.txTextSessionID;
              this.saveDocumentAs(res.document);
              break;
            case DocumentActionPrint:
              res.document = this.document;
              this.printDocument(res.document, 'onFileClicked->ActionPrint');
              break;
            case DocumentActionDownload:
              this.logger.logEvent(this.documentEventLogName, {
                method: 'onFileClicked->ActionDownload',
                document: res.document,
              });
              this.downloadDocument(false);
              break;
            case DocumentActionDownloadAsPdf:
              this.downloadDocument(true);
              break;
            case DocumentActionUpload:
              this.logger.logEvent(this.documentEventLogName, {
                method: 'onFileClicked->ActionUpload',
                document: res.document,
              });
              break;
            case DocumentActionLogout:
              this.logout();
              break;
            case DocumentActionClose:
              this.onCloseClicked();
              break;
            default:
              break;
          }
        }
      })
      .finally(() => {
        document.getElementById('tabDefault')?.click();
        this.isFileMenuOpen = false;
        this.changeDetectorRef.detectChanges();
        this.dialogService.eventCloseDialog();
      }); // activate Home tab
  }

  public printDocument(document: DocumentModel, logMessage: string) {
    this.logger.logEvent(this.documentEventLogName, {
      method: logMessage,
      document,
    });
    let saveDocumentDirtyFlag = this.isDocumentDirty;
    this.changeDetectorRef.detectChanges();
    this.documentService
      .printDocument(document.templateId)
      .pipe(take(1))
      .subscribe({
        next: () => {},
        error: () => {},
        complete: () => {
          this.textControlService.printDocument();
        },
      }); //unsubscribe after one received
  }

  public openDocument(document: DocumentModel | undefined) {
    this.isLoading = true;
    this.checkUnsavedChanges()
      .pipe(take(1)) // Automatic Unsubscribe after one received
      .subscribe((res) => {
        if (res === true) {
          this.isDocumentDirty = false;
          if (document) {
            this.getDocument(document);
          }
        }
      });
  }

  private getDocument(doc: DocumentModel) {
    this.loadingService.setLoadingAction(LoadingAction.Loading);
    this.unsubscribeFromChangedObservableForICode();
    this.unsubscribeFromChangedObservableForAutosave();

    this.documentService
      .getDocument(doc)
      .pipe(
        take(1),
        finalize(() => {
          this.loadingService.resetLoadingDocumentIndicator();
          this.changeDetectorRef.detectChanges();
        })
      ) // Automatic Unsubscribe after one received
      .subscribe({
        next: (res: DocumentModel) => {
          if (res) {
            this.loadDocument(res);
          }
        },
      });
  }

  private getDocumentBySession() {
    const sessionId = this.windowService.getSessionIdFromWindow();
    if (sessionId) {
      this.document.sessionId = sessionId;
      this.loadingService.setLoadingAction(LoadingAction.Loading);
      this.unsubscribeFromChangedObservableForICode();
      this.unsubscribeFromChangedObservableForAutosave();
      this.documentService
        .getDocumentBySession(this.document)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.resetLoadingDocumentIndicator();
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
          })
        ) // Automatic Unsubscribe after one received
        .subscribe({
          next: (res: DocumentModel) => {
            if (res) {
              this.document = res;
              this.loadDocument(this.document);
            } else {
              this.subscribeToChangedObservableForAutosave();
              this.subscribeToChangedObservableForICode();
              this.getAutosavesByStaffId();
            }
          },
        });
    }
  }

  private async loadDocument(documentModel: DocumentModel) {
    this.document = documentModel;
    this.isDocumentDirty = documentModel.isDirty ?? false;
    this.subscribeToChangedObservableForICode();
    this.subscribeToChangedObservableForAutosave();
    this.storeDocumentData(documentModel);
    this.fileName = this.document.filename;
    this.textControlService.showFieldNavigatorSideBar(this.document.isActiveDocument);
    //this.isSideBarOpen will always return false - hiding close button 100% of the time - future feature should query the DOM for if a sidebar is open and toggle the close button respectively
    this.showSideBarCloseButton = this.isSideBarOpen();
    this.documentService.documentWasLoaded(this.document);
    this.textControlService.getDocumentUnderscoreAnswers();
    // this is a time sink and needs to be optimized
    // this is not showing in the ui
    // this.checkUnansweredFormFields();
    this.getAutosavesByCasId();
    this.subscribeToActiveFieldSelected();
    this.getFieldReferences();

    if (documentModel.isActiveDocument && this.documentDataService.getAnswers().length > 0 && !this.isRecovered) {
      this.documentService.setDocumentDisconnected(false);
    }

    this.isLoading = false;
  }

  private focusToFirstEditableField() {
    const allFields = this.textControlService.formFieldsService.allFields;
    for (let i = 0; i < allFields.length; i++) {
      const ff = allFields[i];
      const fd = ff.details;
      if (fd && this.isEditableField(fd.name)) {
        this.textControlService.navigateToFormField(fd.id);
        break;
      }
    }
    this.textControlService.focus();
  }
  private subscribeToActiveFieldSelected() {
    this.textControlService.activeField$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: FormField | undefined) => {
      this.ngZone.run(() => {
        if (res !== undefined) {
          this.isActiveFieldSelected = true;
        } else {
          this.isActiveFieldSelected = false;
        }
        this.changeDetectorRef.detectChanges();
      });
    });
  }

  private checkIfAnswersChanged() {
    if (this.document.answersChanged === true) {
      this.openAnswersChangedDialog();
    } else if (!this.isRecovered) {
      this.focusToFirstEditableField();
      if (this.document.isActiveDocument) {
        window.addEventListener('focus', this.onfocusHandler);
      }
    }
  }

  private openAnswersChangedDialog() {
    const newCaseAnswersDialogRef = this.dialogService.openCustomDialog(
      'answersChanged',
      AnswersChangedDialogComponent,
      {
        backdrop: 'static',
      }
    );

    newCaseAnswersDialogRef.result
      .then((res) => {
        if (res === true) {
          window.addEventListener('focus', this.onfocusHandler);
          this.getAnswersFromPool();
        } else {
          // The document becomes inactive, so we disconnect it from the answer pool
          this.onlineClientService.disconnectSessionAndKeepDocument();
          this.document.isActiveDocument = false;
          if (this.document.documentAnswers) {
            this.documentDataService.setAnswers(this.document.documentAnswers);
          }
        }
      })
      .finally(() => {
        this.textControlService.focus();
        this.focusToFirstEditableField();
      });
  }

  private updateAnswers(): Observable<boolean> {
    console.debug('updateAnswers start');
    console.debug('updateAnswers AnswerPool = ', this.updatingAnswersFromPool);
    return from(this.textControlService.formFieldsService.allFields).pipe(
      mergeMap((ff: FormField) => {
        return this.updateFormFieldAnswer(ff);
      }),
      toArray(),
      map((updatedValues: string[]) => updatedValues.some((value) => value)),
      tap(() => {
        console.debug('updateAnswers End');
        this.loadingService.resetLoadingDocumentIndicator();
        this.changeDetectorRef.detectChanges();
      })
    );
  }

  private updateFormFieldAnswer(ff: FormField): Observable<string> {
    let answer: string | undefined = undefined;
    if (ff.details.name.toLowerCase().startsWith('q')) {
      answer = this.documentDataService.getStoredAnswer(ff.details.name)?.answer;
    } else {
      answer = this.documentDataService.getLogicResultByToken(ff.details.name)?.result;
    }
    if (answer != undefined && !this.areEquivalentPreservingQuoteType(ff.details.text, answer)) {
      console.debug('updateFormFieldAnswer name, text, answer', ff.details.name, ff.details.text, answer);
      const answerWithSmartQuotes = smartenQuotes(answer);
      return from(this.textControlService.formFieldsService.setFieldTextPromise(ff, answerWithSmartQuotes));
    }
    return of('false');
  }

  public saveAsObservable(documentModel: DocumentModel): Observable<any> {
    if (!documentModel.filename) {
      this.onFileClicked(FileMenuActions.SaveAs);
      // if we return false here, checkUnsavedChanges will return the same value
      // we won't proceed with the next action because the user still needs to save the document
      return of(false);
    }

    // because the recovered document still has the id of the original document, we set it to empty string to save it as a new document
    // we don't want to overwrite the original document
    if (this.isRecovered) {
      documentModel.dcmID = '';
    }

    documentModel.autosaveSessionId = sessionStorage.getItem('recover_autosave') ?? undefined;
    // this.document.document = await this.textControlService.saveDocument();
    this.loadingService.setLoadingAction(LoadingAction.Saving);
    documentModel.txTextSessionID = this.textControlService.txTextSessionID;
    this.saveFirmDictionary();

    return this.documentService.saveDocument(documentModel).pipe(
      take(1),
      tap((res: DocumentModel) => {
        this.loadingService.resetLoadingDocumentIndicator();
        this.changeDetectorRef.detectChanges();
        this.toastService.show('Document successfully saved', {
          classname: 'bg-success',
          autohide: true,
        });
        // Delete cache when we successfully save new firm document. We need to get the folder again to be able to see the document in browse
        if (
          (documentModel.dcmID == null || documentModel.dcmID === '') &&
          (documentModel.casID == null || documentModel.casID === '')
        ) {
          this.folderService.deleteCache();
        }
        // assign res to this.document instead of just assigning the filename and dcmID
        // This will trigger OnChanges in the header component, which will refresh the case description when saving a new case document

        if (res.saveAsPdf) {
          // open dialog
          const dialogRef = this.dialogService.openCustomDialog('pdfSaved', PdfSavedDialogComponent);
          dialogRef.componentInstance.document = res;
          dialogRef.result.then((res) => {});
        } else {
          this.document = res;
          if (this.document.filename) {
            document.title = this.document.filename;
          }
          this.documentService.documentWasLoaded(this.document);
        }

        this.isAnswersDirty = false;
        this.isDocumentDirty = false;
        if (this.isRecovered && this.document.autosaveSessionId) {
          this.autosaveService
            .delete(this.document.autosaveSessionId)
            .pipe(take(1))
            .subscribe({
              next: undefined,
              error: undefined,
              complete: () => (this.isRecovered = false),
            });
        }
      }),
      catchError((err) => {
        this.loadingService.resetLoadingDocumentIndicator();
        this.changeDetectorRef.detectChanges();
        return err;
      })
    );
  }

  public async save(document: DocumentModel) {
    if (this.loadingService.loading) {
      if (this.loadingService.loadingAction === LoadingAction.Saving) {
        console.log('Save already in progress.');
        return;
      } else {
        console.log('Another action in progress. Retrying save in 500s');
        setTimeout(() => this.save(document), 500);
      }
    }

    return this.saveAsObservable(document)
      .pipe(take(1))
      .subscribe((res: any) => {});
  }

  private saveDocumentAs(document: DocumentModel | undefined) {
    if (document) {
      // this.document = document;
      // remove the dcmID so it can be saved as another document
      document.dcmID = '';
      this.save(document);
    }
  }

  public newDocument(cas: CaseModel | undefined) {
    this.formFieldModal?.closeModal();
    if (this.isDocumentDirty || this.isAnswersDirty) {
      this.autosaveService.deleteBeacon();
    }
    this.document = {
      isActiveDocument: true,
      saveAsPdf: false,
      answersChanged: false,
      openInAnotherSession: false,
      pdfAnswersChanged: false,
      txdocsAnswersChanged: false,
    };
    this.unsubscribeFromChangedObservableForICode();
    this.unsubscribeFromChangedObservableForAutosave();
    this.textControlService.closeDocument().then(() => {
      this.subscribeToChangedObservableForAutosave();
    });

    if (cas != undefined) {
      this.document.casID = cas.casID;
      this.document.casDescription = cas.description;
      this.document.ctcFirstName = cas.ctcFirstName;
      this.document.ctcLastName = cas.ctcLastName;
      this.document.filename = this.defaultFilename;
      this.document.folder = cas.folder;
    }
    document.title = environment.name;
    this.clearDirty();
  }

  async downloadDocument(downloadAsPdf: boolean) {
    //set the loading flag
    this.loadingService.setLoadingAction(LoadingAction.Downloading);
    this.changeDetectorRef.detectChanges();
    this.logger.logEvent(this.documentEventLogName, {
      method: 'downloadDocument',
      document: this.document,
      asPDF: downloadAsPdf,
    });
    this.textControlService
      .saveDocument()
      .then((doc) => {
        this.documentService
          .downloadDocument(
            this.textControlService.txTextSessionID,
            downloadAsPdf,
            this.document.filename ?? this.defaultFilename,
            this.document.templateId ?? 0,
            this.document.praSysId ?? 0,
            this.documentDataService.getLogicResults()
          )
          .pipe(take(1)) // Automatic Unsubscribe after one received
          .subscribe((res: any) => {
            if (res && res.document) {
              this.download = res.document;
              var element = document.createElement('a');
              element.setAttribute('href', 'data:application/octet-stream;base64,' + this.download);
              element.setAttribute('download', res.fileName);
              element.style.display = 'none';
              document.body.appendChild(element);

              // simulate click
              element.click();

              // remove the link
              document.body.removeChild(element);
            }
          });
      })
      .finally(() => {
        //turn the loader off
        this.loadingService.resetLoadingDocumentIndicator();
        this.changeDetectorRef.detectChanges();
      });
  }

  onCloseClicked() {
    if (this.document.pathname) {
      this.closeDocument();
    } else {
      this.logout();
    }
  }

  closeDocument() {
    this.checkUnsavedChanges()
      .pipe(take(1)) // Automatic Unsubscribe after one received
      .subscribe((res) => {
        if (res === false) {
          return;
        }
        this.isDocumentDirty = false;
        this.autosaveService.deleteBeacon();
        this.documentService.setDocumentDisconnected(false);
        if (window.opener != null || window.history.length == 1) {
          this.onlineClientService.disconnectWithLogout();
          window.close();
        } else {
          this.onlineClientService.disconnect().subscribe((res) => {
            this.clearOnCloseDocument();
            this.newDocument(undefined);
          });
        }
      });
  }

  private clearOnCloseDocument() {
    this.documentDataService.clearAll();
    this.textControlService.clearActiveField();
    this.textControlService.clearAllFields();
    //This is initialized when new case answers available dialog answered 'stay in sync'
    window.removeEventListener('focus', this.onfocusHandler);
    this.formFieldModal!.closeModal();
    this.textControlService.showFieldNavigatorSideBar(false);
    this.formFieldModal!.docked = true;
    this.clearDirty();
    this.documentService.setDocumentDisconnected(true);
    this.changeDetectorRef.detectChanges();
  }

  private clearDirty() {
    this.isDocumentDirty = false;
    this.isAnswersDirty = false;
  }

  public selectFile(e: FileList) {
    const file = e.item(0);
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          const base64 = reader.result?.toString().replace(/^data:(.*,)?/, '') || ''; // remove file meta data from the string
          const document: DocumentModel = {
            isActiveDocument: false,
            saveAsPdf: false,
            answersChanged: false,
            document: base64,
            filename: file.name,
            openInAnotherSession: false,
            pdfAnswersChanged: false,
            txdocsAnswersChanged: false,
          };
          this.openUploadDialog(document);
        }
      };
    }
  }

  private getFieldReferences() {
    const startTime = performance.now(); // Start the timer
    this.textControlService.formFieldsService
      .getAllFormFieldsReferencesObservable(this.document.allFields ?? [])
      .pipe(take(1))
      .subscribe((res) => {
        this.textControlService.formFieldsService.allFields = res;
        this.document.allFields = []; // remove them to avoid bloating the request when saving
        //this will update all fields
        const endTime = performance.now(); // End the timer
        const duration = endTime - startTime; // Calculate the duration
        console.debug(`onDocumentLoaded ==>Time taken: ${duration} milliseconds`); // Log the duration
        this.checkIfAnswersChanged();
      });
  }

  public onWebSocketClosed(e: any) {
    if (!e.wasClean) {
      this.textControlRetryCount++;
      // only retries 10 times then quits
      // it sometimes stops retrying before 10 times, so we want the email sent after 5 retries
      if (this.textControlRetryCount >= 5) {
        this.textControlRetryCount = 0; //reset control var
        //event contains; code(number), reason(string) and wasClean(bool)
        this.sessionService.sendNotificationBeacon(e);

        this.websocketOpened = false;
        this.changeDetectorRef.detectChanges();
        this.dialogService.openCustomDialog('WebsocketClosedDialog', WebsocketClosedDialogComponent, {
          size: 'xl',
          backdrop: 'static',
          keyboard: false,
          windowClass: 'websocket-closed-modal',
        });
      }
    }
  }

  public onFileDropped(e: any) {
    // if an image is copied and pasted, that is counted as a file drop
    // we want to let that through
    if (e.fileType.startsWith('image')) {
      return e;
    }

    e.cancel = true;
    this.toastService.show('To upload a document drag and drop it into the upload area.', {
      classname: 'bg-danger',
      autohide: false,
    });
    const ref = this.openFileMenu();
    ref.componentInstance.preselectedComponent = FileMenuActions.Upload;
    this.changeDetectorRef.detectChanges();
  }

  private openUploadDialog(document: DocumentModel) {
    const ref = this.openFileMenu();
    ref.componentInstance.uploadedDocument = document;
    ref.componentInstance.preselectedComponent = FileMenuActions.Upload;
  }

  public onFieldLeft(field: TextFieldEventArgs) {
    console.debug(
      'onFieldLeft field, id, activeSelectedField name',
      field.textField.name,
      field.textField.id,
      this.textControlService.formFieldsService.activeSelectedField?.details?.name ?? 'no field'
    );
    if (!field) {
      this.ngZone.run(() => {
        this.closeFormFieldModal();
      });
    }
  }

  // possibly calling updateSelectedfield twice
  // once here and another on selection position change
  public onFieldEntered(field: TextFieldEventArgs) {
    console.debug(
      'onFieldEntered field, activeSelectedField name',
      field,
      this.textControlService.formFieldsService.activeSelectedField?.details?.name ?? 'no field'
    );
    if (!field.textField.editable || this.updatingLogic) {
      return;
    }
    // prevent opening the form field dialog if the user is selecting text that contains the form field
    this.UpdateUIForSelectedField();
  }

  private UpdateUIForSelectedField() {
    if (this.textControlService.textSelectLength === 0) {
      this.currentInputValue = undefined;
      this.textControlService.forceUpdateSelectedField().subscribe({
        next: () => {
          this.ngZone.run(() => {
            console.debug('CMDEBUG OPENING MODAL');
            this.openModal();
            this.changeDetectorRef.detectChanges();
          });
        },
      });
    }
  }

  public onFormFieldChanged(e: any) {
    console.log(`entered onFormFieldChanged with: ${e}`);
    // when the field is deleted, don't save the value to the answer pool and the other fields
    // if the field has been deleted, the value of start is -1 and the bounds object has 0 values
    // if we are updating the field from logic or answer pool, also don't do anything
    if (e.formField.start === -1 || this.updatingLogic === true || this.updatingAnswersFromPool === true) {
      return;
    }
    if (this.documentDataService.isAnswerChanged(e.formField.name, e.formField.text)) {
      this.ngZone.run(() => {
        this.onFormFieldValueChanged(e.formField.name, e.formField.text, e.formField.id);
        this.documentDataService.updateAnswer(e.formField.name, e.formField.text);
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  public onNavigateToNextFormField(event: number) {
    if (this.txcontrolNavigationInProcess) {
      return;
    }
    this.txcontrolNavigationInProcess = true;
    this.textControlService
      .navigateToNextFormField(event, this.showUnansweredOnly)
      .then((res: Boolean) => {
        if (res) {
          this.ngZone.run(() => {
            this.openModal();
          });
        }
        // TODO move this code to text control service?
        // this.toastService.show(`You reached the ${event === -1 ? 'beginning' : 'end'} of the document`);
      })
      .finally(() => {
        this.txcontrolNavigationInProcess = false;
        if (this.textControlService.isFormFieldFocused) {
          this.formFieldModal?.focusInput();
          return;
        }
        this.textControlService.focus();
      });
  }

  public addCustomMenuItems() {
    this.ngZone.run(() => {
      const menu = document.querySelector('ul.tabs');
      if (menu) {
        menu.insertAdjacentHTML('afterbegin', txdocsMenuItem);
        menu.insertAdjacentHTML('afterbegin', fileMenuItem);

        this.elRef.nativeElement.querySelector('#tabFile').addEventListener('click', this.onFileClicked.bind(this, ''));
        this.elRef.nativeElement
          .querySelector('#tabTxdocs')
          .addEventListener(
            'click',
            this.textControlService.selectRibbonTab.bind(this.textControlService, 'tabTxdocs')
          );
      }

      const contentContainer = document.getElementById('txRibbonTabContentContainer');
      if (contentContainer) {
        contentContainer.insertAdjacentHTML('afterbegin', txdocsTab);
        contentContainer.insertAdjacentHTML('afterbegin', fileTab);
      }
    });
  }

  public onTextControlLoaded() {
    this.txTextControlLoaded = true;
    this.addTXTextControlEventListeners();
    this.ngZone.run(async () => {
      this.checkShowIntroDialog();
      if (globalThis.TXTextRenderingPrinter) {
        TXTextControl.setFormattingPrinter(globalThis.TXTextRenderingPrinter);
      }
      TXTextControl.setRenderMode(TXTextControl.ComponentRenderMode.Vector);
      this.subscribeToChangedObservableForAutosave();
      this.subscribeToChangedObservableForICode();
      this.subscribeToFieldDeletedObservable();
      this.documentService.setDocumentDisconnected(true);
      this.loadingService.setLoadingAction(LoadingAction.Loading);
      this.changeDetectorRef.detectChanges();
      this.sessionService.checkLicenseCount();
      const sessionId = this.windowService.getSessionIdFromWindow();

      if (sessionId) {
        let recoverSessionId = sessionStorage.getItem('recover_autosave') ?? null;
        const resumeSessionId = sessionStorage.getItem('resume_sessionId') ?? null;

        if (recoverSessionId) {
          this.isRecovered = true;
          this.getAutosaveBySessionid(recoverSessionId);
        } else if (this.refreshSessionId) {
          recoverSessionId = this.refreshSessionId;
          this.getRefreshBySessionId(this.refreshSessionId, this.refreshDcmId);
        } else if (resumeSessionId) {
          this.resumeSessionById(sessionId);
        } else {
          const casId = sessionStorage.getItem('caseId');
          const ctcId = sessionStorage.getItem('contactId');

          if (casId && ctcId) {
            this.setDocumentContactAndCase(ctcId, casId);
            this.loadingService.resetLoadingDocumentIndicator();
          } else {
            this.getDocumentBySession();
          }
        }
      }
      this.addCustomMenuItems();
      await this.textControlService.initializeTextControl();
      this.websocketOpened = true;
      this.openModal();
    });
    //region This is an example of how to highlight a snippet in the document
    // DO NOT DELETE
    // THIS IS AN EXAMPLE OF HIGHLIGHTING / INTERACTING WITH THE DOCUMENT
    // USING A DOM ELEMENT
    // var sub = combineLatest([
    //   this.textControlService.watchDocumentAndSelectionChangeObservable(),
    //   this.textControlService.position$,
    // ])
    //   .pipe(takeUntil(this.unsubscribe$))
    //   .subscribe(async (event) => {
    //     let txContext = new TextControlContext();

    //     //this.position = event[1];
    //     this.selection = event[0].selection;
    //     this.InputPosition = event[0].position;
    //     let location = event[1].location;
    //     let zoom = event[1].zoom / 100;
    //     this.textSelected = this.selection.length != 0;

    //     // get the Start character bounds
    //     let startChar = await txContext.textChars.elementAt(event[0].selection.start);
    //     let textCharStart = new TextChar(startChar);
    //     let boundsStart = await textCharStart.getBounds();

    //     let mainCanvas = document.getElementById('mainCanvas');
    //     if (!mainCanvas) {
    //       console.error('Canvas element not found.');
    //       return;
    //     }
    //     const rect = mainCanvas.getBoundingClientRect();
    //     let newRect = new DOMRect();
    //     newRect.x = rect.left + (boundsStart.location.x / 15 - location.x) * zoom;
    //     newRect.y = rect.top + (boundsStart.location.y / 15 - location.y) * zoom;
    //     newRect.width = (boundsStart.size.width / 15) * zoom;
    //     newRect.height = (boundsStart.size.height / 15) * zoom;
    //     let snippetMarkerElement = document.getElementById('snippet-marker');
    //     if (snippetMarkerElement) {
    //       snippetMarkerElement.style.left = newRect.x + 'px';
    //       snippetMarkerElement.style.top = newRect.y + 'px';
    //       snippetMarkerElement.style.width = newRect.width + 'px'; // '20px';
    //       snippetMarkerElement.style.height = newRect.height + 'px'; //'5px';
    //     }
    //   });
    //endregion
  }

  public async onFormFieldValueChanged(name: string, value: string, id: number = 0) {
    console.debug('onFormFieldValueChanged', name, value, id);
    await lastValueFrom(this.onFormFieldValueChangedObs(name, value, id));
    console.debug('Finishing Callback onFormFieldValueChanged Name: ' + name + ' Text: ' + value);
  }

  public onFormFieldValueChangedObs(name: string, value: string, id: number = 0) {
    //TXTextControl.beginUndoAction('Field Change', () => {
    this.isAnswersDirty = true;
    this.updatingLogic = true;
    let objectsToUpdate: Array<ObjectModel> = [];
    if (name.toLowerCase().startsWith('i')) {
      const object = this.documentDataService.getObject(name);
      if (object && object.qToken) {
        name = object.qToken;
      }
    }
    const normalizedValue = this.normalizeQuotes(value);
    const smartQuoteValue = smartenQuotes(value);
    this.documentDataService.updateAnswer(name, normalizedValue);
    this.textControlService.rebuildCaseDictionary();
    objectsToUpdate = this.documentDataService
      .getObjectsByKey('I')
      .concat(this.documentDataService.getObjectsByKey('C'))
      .filter((o) => o.logic.includes(name.substring(0, 6)) || !o.logic.includes('Q'));
    const tokensToUpdate: Array<string> = this.textControlService.formFieldsService.allFields
      .filter((ff) => objectsToUpdate.some((o) => ff.details.name.includes(o.token)))
      .map((ff) => ff.details.name);
    // we want to update all the I codes that we don't know what Q code they depend on

    const answer: AnswerModel = {
      token: name,
      answer: normalizedValue,
      answerChoice: '',
      dimIndex: '',
      praSysId: this.document.praSysId,
    };
    this.unsubscribeFromChangedObservableForICode();

    return this.textControlService.setFormFieldValueObs(name, smartQuoteValue).pipe(
      tap(() => {
        console.debug('formFieldValueChange => setFormFieldValueObs', name, smartQuoteValue);
        console.debug('ffvc Obs:setFormFieldValueObs', name, smartQuoteValue);
      }),
      concatMap((res) => {
        console.debug('ffvc Obs:executeLogic', name, value, objectsToUpdate, this.documentDataService.getAnswers());
        return this.logicService.executeLogic(
          name,
          normalizedValue,
          objectsToUpdate,
          this.documentDataService.getAnswers(),
          this.document.praSysId,
          this.document.templateId,
          tokensToUpdate,
          this.document.title
        );
      }),
      concatMap((res: LogicResponseModel) => {
        console.debug('ffvc Obs:updateLogicResults', res.newValues);
        this.documentDataService.updateLogicResults(res.newValues);
        return this.updateITokens(res.newValues);
      }),
      concatMap((res) => {
        console.debug('ffvc Obs:saveAnswerToPools');
        return this.documentDataService.saveAnswerToPool(answer);
      }),
      tap((res) => {
        console.debug('ffvc Obs:removeAnswerLock', res);
        // remove answer lock when answers are saved
        this.answerLockService.removeAnswerLock(this.document.casID);
      }),
      tap(() => {
        this.ngZone.run(() => {
          console.debug('ffvc Obs:onFormFieldValueChanged', this.updatingLogic);
          console.debug('ffvc Obs:END setFormFieldValueObs');
          this.updatingLogic = false;
          // todo: THIS IS NOT SHOWING IN THE UI  WHY?
          // this.checkUnansweredFormFields();
          this.subscribeToChangedObservableForICode();
          this.textControlService.formFieldsService.startNotifications();
          this.UpdateUIForSelectedField();
          //this.textControlService.loading = false;
          //TXTextControl.endUndoAction();
          this.changeDetectorRef.detectChanges();
        });
      })
    );
  }
  public logout() {
    this.checkUnsavedChanges()
      .pipe(take(1))
      .subscribe((res) => {
        if (res === true) {
          this.isDocumentDirty = false;
          this.onlineClientService
            .disconnect() // dont logout yet cause the logout below will take care of it.
            .pipe(take(1))
            .subscribe((res) => {
              this.removeWindowEventListeners();
              this.autosaveService
                .delete(this.windowService.getSessionIdFromWindow() ?? '')
                .pipe(take(1))
                .subscribe((res) => {
                  this.windowService.clearSessionStorage();
                  this.authenticationService.logout();
                });
            });
        } else {
          this.toastService.show('You have unsaved changes. Please save the document before you logout.', {
            classname: 'bg-warning',
            autohide: true,
          });
        }
      });
  }

  private updateITokens(newData: LogicResultModel[]): Observable<any> {
    console.debug('updateITokens', newData);
    return from(this.textControlService.formFieldsService.allFields).pipe(
      mergeMap((ff: FormField) => {
        if (ff && ff.details && newData) {
          const result = newData.find(r => r.token === ff.details.name)
          if (result != null) {
            const smartQuoteValue = smartenQuotes(result.result);

            if (smartQuoteValue != ff.details.text) {
                return from(this.textControlService.formFieldsService.setFieldTextPromise(ff, result.result));
            }         
          }
        }
        return of('false');
      }),
      toArray(),
      map((updatedValues: string[]) => updatedValues.some((value) => value))
    );
  }

  public checkUnsavedChanges(): Observable<boolean> {
    if (this.isAnswersDirty || this.isDocumentDirty) {
      var saved = new Subject<boolean>();
      const unsavedChangesDialogRef = this.dialogService.openCustomDialog(
        'UnsavedChanges',
        UnsavedChangesDialogComponent,
        {
          backdrop: 'static',
        }
      );

      unsavedChangesDialogRef.result.then((res: boolean) => {
        if (res === true) {
          this.saveAsObservable(this.document)
            .pipe(take(1))
            .subscribe({
              next: (res: any) => {
                if (res === false) {
                  saved.next(false);
                  // don't proceed
                } else {
                  saved.next(true);
                  // document was saved, proceed
                }
              },
              error: (err: any) => saved.next(false),
              complete: () => saved.complete(),
            });
        } else {
          //user interaction indicates they dont want changes so mark document as not "dirty"
          // this.isDocumentDirty = false;
          saved.next(true);
        }
      });
      return saved.asObservable();
    }

    // true means there are no unsaved changes, so we can proceed with the next action
    return of(true);
  }

  public closeFormFieldModal() {
    this.ngZone.run(() => {
      this.formFieldModal?.closeModal();
    });
  }

  private openModal() {
    this.ngZone.run(() => {
      console.debug('openModal', this.textControlService.formFieldsService.activeSelectedField);
      // Use the activeField$ observable instead of activeSelectedField
      // activeSelectedField is not immediately updated on the textFieldEntered event
      // we shouldn't force update it, because that messes up field value updating on field navigation
      this.textControlService.activeField$.pipe(take(1)).subscribe((field) => {
        if (field && field.details) {
          this.formFieldModal?.openModal();
        }
      });
    });
  }

  private addWindowEventListeners() {
    window.addEventListener('keyup', this.onKeyUpHandler);
    window.addEventListener('keydown', this.onKeyDownHandler, true);
    window.addEventListener('blur', this.onblurHandler);
    window.addEventListener('pagehide', this.onPageHideHandler);
  }

  private removeWindowEventListeners() {
    window.removeEventListener('keyup', this.onKeyUpHandler);
    window.removeEventListener('keydown', this.onKeyDownHandler);
    window.removeEventListener('focus', this.onfocusHandler);
    window.removeEventListener('blur', this.onblurHandler);
    window.removeEventListener('pagehide', this.onPageHideHandler);
  }

  private getAnswersFromPool() {
    const sessionId = this.windowService.getSessionIdFromWindow();
    if (!sessionId) {
      return;
    }
    this.documentDataService
      .getAnswersFromPool(this.document)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (res && res.answers) {
            this.documentDataService.updateAnswers(res.answers);
            this.textControlService.rebuildCaseDictionary();
            if (res.logicResults) {
              this.documentDataService.setLogicResults(res.logicResults);
            }
            const objectsToUpdate = this.documentDataService.getIcodesToBeUpdated();
            const tokensToUpdate: Array<string> = this.textControlService.formFieldsService.allFields
              .filter((ff) => objectsToUpdate.some((o) => ff.details.name.includes(o.token)))
              .map((ff) => ff.details.name);
            // TODO refactor this so we don't have to call logic when we have no objects/tokensToUpdate
            this.updatingAnswersFromPool = true;
            this.logicService
              .executeLogic(
                '',
                '',
                objectsToUpdate,
                this.documentDataService.getAnswers(),
                this.document.praSysId,
                this.document.templateId,
                tokensToUpdate,
                this.document.title
              )
              .pipe(take(1))
              .subscribe((res: LogicResponseModel) => {
                if (res) {
                  this.documentDataService.updateLogicResults(res.newValues);
                  this.updateAnswers()
                    .pipe(
                      take(1),
                      tap((res) => {})
                    )
                    .subscribe((res) => {
                      this.ngZone.run(() => {
                        this.updatingAnswersFromPool = false;
                        console.debug('Getting answers from pool complete');
                        // force the side panel to update value
                        this.openModal();
                      });
                    });
                }
              });
          }
        },
        error: (error) => {
          //INTERNAL USE ONLY - DO NOT DISPLAY ERRORS TO END USER
          // this.toastService.show(error, {
          //   classname: 'bg-danger',
          // });
        },
      });
  }

  private tryGettingAnswersFromPool() {
    const currentTimestamp = Date.now();
    console.debug('tryGettingAnswersFromPool', currentTimestamp);
    const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
    if (answersLocked == null) {
      this.getAnswersFromPool();
    } else {
      this.loadingService.loading = true;
      this.loadingService.loadingAction = LoadingAction.Updating;
      this.changeDetectorRef.detectChanges();

      const differenceInSeconds = (currentTimestamp - (answersLocked.timestamp ?? 0)) / 1000;
      if (differenceInSeconds >= 10) {
        console.error('WTF');
        // something failed, remove lock and log to app insights
        this.logger.logException({
          name: 'AnswerLockError',
          message:
            'An error has occured and there has been an answer lock for 10 seconds or longer. ' +
            JSON.stringify(answersLocked),
        });
        this.answerLockService.forceRemoveAnswerLock(this.document.casID);
        this.getAnswersFromPool();
      } else if (answersLocked.sessionId === this.windowService.getSessionIdFromWindow()) {
        this.answerLockService.removeAnswerLock(this.document.casID);
        this.getAnswersFromPool();
      } else {
        // retry
        setTimeout(() => this.tryGettingAnswersFromPool(), 5);
      }
    }
  }

  private onRibbonFindClickHandler(event: Event) {
    //alert('Custom Click Handler');
    event.stopImmediatePropagation();
    this.dialogService.openFindReplaceDialog('find', () => {
      setTimeout(() => {
        TXTextControl.setEditMode(TXTextControl.EditMode.Edit);
        TXTextControl.setActivationState(TXTextControl.ActivationState.Activated);
        TXTextControl.focus();
      }, 100);
    });

    this.changeDetectorRef.detectChanges();

    return false;
  }

  private onRibbonReplaceClickHandler(event: Event) {
    //alert('Custom Click Handler');
    event.stopImmediatePropagation();
    this.dialogService.openFindReplaceDialog('replace', () => {
      setTimeout(() => {
        TXTextControl.setEditMode(TXTextControl.EditMode.Edit);
        TXTextControl.setActivationState(TXTextControl.ActivationState.Activated);
        TXTextControl.focus();
      }, 100);
    });

    this.changeDetectorRef.detectChanges();

    return false;
  }

  private onFocus() {
    this.ngZone.run(() => {
      const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
      if (answersLocked) {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Updating;
        this.changeDetectorRef.detectChanges();
      }
      this.tryGettingAnswersFromPool();
    });
  }

  private onblur() {
    this.ngZone.run(() => {
      const answersLocked = this.answerLockService.getAnswerLock(this.document.casID);
      if (answersLocked && answersLocked.sessionId !== this.windowService.getSessionIdFromWindow()) {
        return;
      }
      const currentTimestamp = Date.now();
      console.debug(
        'onblur timestamp, formfieldFocused, isActiveFieldSelected',
        currentTimestamp,
        this.textControlService.isFormFieldFocused,
        this.isActiveFieldSelected
      );
      try {
        if (this.textControlService.isFormFieldFocused && this.isActiveFieldSelected) {
          this.handleUpdatingSameFieldsByActiveFormField();
        } else {
          this.textControlService
            .forceUpdateSelectedField()
            .pipe(take(1))
            .subscribe({
              complete: () => {
                this.handleUpdatingSameFieldsByActiveFormField();
              },
            });
        }
      } catch (e) {
        console.error(e);
      } finally {
        // this.removeAnswerLock();
      }
    });
  }
  private getOnBlurUpdatedFormField(): FormField | undefined {
    const activeField = this.textControlService.formFieldsService.activeSelectedField;
    let returnItem: FormField | undefined;

    if (activeField && activeField.details) {
      returnItem = {
        details: activeField?.details,
        index: activeField.index,
        element: activeField.element,
        positioning: activeField.positioning,
        total: activeField.total,
      };
      const obj = this.documentDataService.getObject(activeField.details.name);
      if (obj && obj.objType == 'I' && obj.qToken) {
        console.debug('onblur with activeFieldChanged as Icode', obj.qToken, this.formFieldModal?.value ?? '');
        returnItem.details.name = obj.qToken;
        returnItem.details.text = this.formFieldModal?.value ?? '';
        //  need to save here and not on blur of the side panel element to force the answerlock save
      } else {
        console.debug('onblur with activeFieldChanged as QCode', activeField.details.name, activeField.details.text);
        returnItem.details.name = activeField.details.name;
        returnItem.details.text = activeField.details.text;
      }
    }
    return returnItem;
  }

  private callOnFormFieldValueChangedObs(formField: FormField) {
    this.onFormFieldValueChangedObs(formField.details.name, formField.details.text)
      .pipe(take(1))
      .subscribe({
        error: (err) => {},
        complete: () => {},
      });
  }

  private handleUpdatingSameFieldsByActiveFormField() {
    this.ngZone.run(() => {
      var formField = this.getOnBlurUpdatedFormField();

      if (!formField || !formField.details || !formField.details.name || !formField.details.text) {
        return;
      }
      if (!this.documentDataService.isAnswerChanged(formField.details.name, formField?.details.text)) {
        return;
      }
      this.callOnFormFieldValueChangedObs(formField);
    });
  }

  private checkIfFormFieldDoesNotMatchAnswer(activeField: FormField, answer: string): Boolean {
    if (activeField.details.name.startsWith('I')) {
      var obj = this.documentDataService.getObject(activeField.details.name);
      if (obj && obj.qToken) {
        var result = this.documentDataService.getStoredAnswer(obj.qToken);
        if (result?.answer !== answer) {
          return true;
        }
      }
    } else if (activeField.details.text !== answer) {
      return true;
    }
    return false;
  }

  private onPageHide(e: PageTransitionEvent) {
    // the page is entering the bfcache and may be restored,
    // so don't tear the session down
    if (e.persisted) {
      return;
    }

    // don't send disconnect request if inside of an iframe
    // we don't want the session logged out when the iframe is removed
    if (window.self === window.top) {
      this.onlineClientService.disconnectWithLogout();
    }

    document.removeEventListener('keydown', this.onKeyDownHandler);
    document.removeEventListener('keyup', this.onKeyUpHandler);
  }

  public answerChangedOnSidePanel($event: any) {
    console.debug('Locking Answers from Side Panel');
    this.answerLockService.createAnswerLock(this.document.casID);
  }

  public onUnansweredValueChanged(event: any) {
    this.showUnansweredOnly = event.target.checked;
  }

  public onNextClicked() {
    this.onNavigateToNextFormField(1);
  }

  public onPreviousClicked() {
    this.onNavigateToNextFormField(-1);
  }

  private checkUnansweredFormFields() {
    this.textControlService.countUnansweredFormFields().then((res: number) => {
      this.unansweredCount = res;
    });
  }

  public onSearchAnswersClicked(searchTerm: string) {
    this.answerSearch = searchTerm;
    this.textControlService.searchFormFields(this.answerSearch);
  }

  public async onKeydown(e: any) {
    if (this.dialogService.isAnyDialogOpen() || this.isFileMenuOpen == true) {
      return;
    }
    if (e.ctrlKey && e.shiftKey && e.which === 68) {
      //"D"
      e.preventDefault();
      this.openDebugDialog();
      e.stopPropagation();
    } else if (e.key === "'" && document.activeElement?.id == 'txHiddenTextField_textView') {
      e.preventDefault();
      e.stopPropagation();
      TXTextControl.selection.setText('’');
    } else if (e.key === '"' && document.activeElement?.id == 'txHiddenTextField_textView') {
      // prevent double quotes from being entered in the text control
      e.preventDefault();
      e.stopPropagation();
      await this.textControlService.insertSmartDoubleQuotes();
    } else if (e.ctrlKey && e.shiftKey && e.which === 86) {
      //"V" - handle no format pasting
      e.preventDefault();
      e.stopPropagation();
      const clipboardItem = await this.clipboardService.getClipboardItem();
      this.clipboardService.createClipboardPasteEvent(clipboardItem, true);
    } else if (e.ctrlKey && e.shiftKey && e.which === 74) {
      //"J" - prevent page break
      e.preventDefault();
      e.stopPropagation();
    } else if (e.ctrlKey) {
      await this.handleCtrlKeyShortcuts(e);
    } else if (e.shiftKey && e.which === 9) {
      //catch shift+tab first
      // shift + tab
      this.handleShiftTabNavigation(e, -1);
    } else if (e.keyCode === 9) {
      // tab
      this.handleTabNavigation(e, 1);
    } else if (e.key === 'Enter') {
      this.handleEnterKeyPress(e);
    } else if (this.isInsideCalculatedField() && this.isASCIIKeyStroke(e)) {
      this.checkIfIcodeChanged();
      e.preventDefault();
      e.stopPropagation();
    } else if (this.isInsideFormattedField()) {
      console.debug('inside formatted field');
      this.preventFieldEditsAndForceFocusToNavigationPanel();
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private isASCIIKeyStroke(e: any) {
    return (e.key.length === 1 && e.key !== ' ') || (e.key === ' ' && e.keyCode === 32);
  }

  private isInsideFormattedField() {
    if (
      !this.textControlService.formFieldsService.activeSelectedField ||
      this.textControlService.isFormFieldFocused ||
      document.activeElement?.id !== 'txHiddenTextField_textView'
    )
      return false;

    const currentFieldObject = this.documentDataService.getObject(
      this.textControlService.formFieldsService.activeSelectedField.details.name
    );
    console.log(
      `isInsideFormattedField has this currenFieldObject: ${JSON.stringify(
        currentFieldObject?.mask
      )} -- checking for a mask`
    );
    if (!currentFieldObject) return false;

    return (
      currentFieldObject.token.startsWith('Q') && currentFieldObject.mask !== null && currentFieldObject.mask !== ''
    );
  }

  private async handleCtrlKeyShortcuts(e: any) {
    switch (e.which) {
      case 66: // ctrl + b
        this.textControlService.setBold();
        e.preventDefault();
        e.stopPropagation();
        break;
      case 67: // ctrl + c
        e.preventDefault();
        e.stopPropagation();
        await this.onRibbonCopyButtonClicked(e);
        break;
      case 88: // ctrl + x
        e.preventDefault();
        e.stopPropagation();
        await this.onRibbonCutButtonClicked(e);
        break;
      case 86: // ctrl + v
        this.textControlService.removeEventListener('changed', this.pasteDocumentChangedEventHandler);
        await this.onRibbonPasteButtonClicked(e);
        break;
      case 70: // ctrl + f
        //document.getElementById('ribbonTabHome_btnFind')?.click();
        this.dialogService.openFindReplaceDialog('find', () => {
          setTimeout(() => {
            TXTextControl.setEditMode(TXTextControl.EditMode.Edit);
            TXTextControl.setActivationState(TXTextControl.ActivationState.Activated);
            TXTextControl.focus();
          }, 100);
        });

        this.changeDetectorRef.detectChanges();
        e.preventDefault();
        e.stopPropagation();
        break;
      case 73: // ctrl + i //
        this.textControlService.setItalic();
        e.preventDefault();
        e.stopPropagation();
        break;
      case 79: // ctrl + o
        this.onFileClicked(FileMenuActions.Open);
        e.preventDefault();
        e.stopPropagation();
        break;
      case 80: // ctrl + p
        this.printDocument(this.document, 'ctrl+P->ActionPrint'); //add meta data records
        e.preventDefault();
        e.stopPropagation();
        break;
      case 83: // ctrl + s
        e.preventDefault();
        e.stopPropagation();

        this.handleSaveEvent();
        break;
      case 85: // ctrl + u
        this.textControlService.setUnderline();
        e.preventDefault();
        e.stopPropagation();
        break;
      default:
        break;
    }
  }

  private handleDocumentSave() {
    if (this.isRecovered) {
      this.onFileClicked(FileMenuActions.SaveAs);
    } else {
      this.save(this.document);
    }
  }

  private handleSaveEvent() {
    var formField = this.getOnBlurUpdatedFormField();
    if (formField && formField.details && formField.details.name && formField.details.text) {
      this.onFormFieldValueChangedObs(formField.details.name, formField.details.text)
        .pipe(take(1))
        .subscribe({
          error: (err) => {},
          complete: () => {
            this.handleDocumentSave();
          },
        });
      return;
    }
    this.handleDocumentSave();
  }

  private handleTabNavigation(e: any, direction: number) {
    if (
      this.textControlService.formFieldsService.currentSelectedFieldId !== undefined &&
      this.textControlService.formFieldsService.currentSelectedFieldId !== -1
    ) {
      this.onTabNavigation(e, direction);
    }
  }

  private handleShiftTabNavigation(e: any, direction: number) {
    if (this.textControlService.formFieldsService.currentSelectedFieldId) {
      this.onTabNavigation(e, direction);
    } else {
      this.textControlService.decreaseIndent();
    }
  }

  private handleEnterKeyPress(e: any) {
    if (
      this.textControlService.formFieldsService.activeSelectedField &&
      this.textControlService.formFieldsService.activeSelectedField.details &&
      !this.textControlService.isFormFieldFocused
    ) {
      let obj = null;
      if (this.textControlService.formFieldsService.activeSelectedField.details.name.startsWith('Q')) {
        obj = this.documentDataService.getObject(
          this.textControlService.formFieldsService.activeSelectedField.details.name
        );
      } else if (this.textControlService.formFieldsService.activeSelectedField.details.name.startsWith('I')) {
        obj = this.documentDataService.getObject(
          this.textControlService.formFieldsService.activeSelectedField.details.name
        );
        if (obj != null && obj.qToken != null && obj.qToken !== '' && obj.qToken.startsWith('Q')) {
          obj = this.documentDataService.getObject(obj.qToken);
        }
      }
      if (obj && obj.token.startsWith('Q') && obj.rows <= 1) {
        e.preventDefault();
        e.stopPropagation();
        this.toastService.show('Cannot insert new line here.', {
          classname: 'bg-warning',
          autohide: true,
        });
      }
    }
  }

  public async onKeyup(e: any) {
    const formFieldValue = document.getElementById('formFieldValue');
    if (document.activeElement !== formFieldValue) {
      console.debug(
        'ONKEYUP SHOULD SHOW ACTIVE FIELD',
        this.textControlService.formFieldsService.activeSelectedField?.details?.name ?? 'no field'
      );
      this.textControlService.activeField$.pipe(take(1)).subscribe((formField: FormField | undefined) => {
        console.debug(
          'ONKEYUP FOUND FIELD',
          this.textControlService.formFieldsService.activeSelectedField?.details?.name ?? 'no field',
          formField
        );
        if (formField && formField.details) {
          const obj = this.documentDataService.getObject(formField.details.name);
          if (obj && obj.hasTransformations) {
            console.debug(
              'ONKEYUP has transformations',
              this.textControlService.formFieldsService.activeSelectedField?.details?.name ?? 'no field',
              formField
            );
            return;
          }
          console.debug('ONKEYUP OPEN MODAL formfield Array Value', formField.details.text);
          this.answerLockService.createAnswerLock(this.document.casID);
          this.currentInputValue = formField.details.text;
          this.changeDetectorRef.detectChanges();
        }
      });
    }

    if (e.key === 'Enter' && e.shiftKey === false) {
      await this.textControlService.setContinueNumberingOnCurrentParagraph();
    }
  }

  private onTabNavigation(e: any, step: number) {
    //-1 is essentially the same as undefined
    if (
      this.textControlService.formFieldsService.currentSelectedFieldId !== -1 &&
      !this.textControlService.isFormFieldFocused
    ) {
      e.stopPropagation();
      e.preventDefault();
      this.onNavigateToNextFormField(step);
    }
  }

  initSideBarCustomization() {
    this.ngZone.run(() => {
      const ribbonTabHomeBtnFind = document.getElementById('ribbonTabHome_btnFind');
      if (ribbonTabHomeBtnFind) {
        this.ngZone.run(() => {
          ribbonTabHomeBtnFind.addEventListener('click', this.onRibbonFindClickHandler.bind(this), true);
        });
      }

      const ribbonTabHomeBtnReplace = document.getElementById('ribbonTabHome_btnReplace');
      if (ribbonTabHomeBtnReplace) {
        this.ngZone.run(() => {
          ribbonTabHomeBtnReplace.addEventListener('click', this.onRibbonReplaceClickHandler.bind(this), true);
        });
      }

      const ribbonTabHomeBtnCopy = document.getElementById('ribbonTabHome_btnCopy');
      if (ribbonTabHomeBtnCopy) {
        this.ngZone.run(() => {
          ribbonTabHomeBtnCopy.addEventListener('click', this.onRibbonCopyButtonClicked.bind(this));
        });
      }

      const ribbonTabHomeBtnCut = document.getElementById('ribbonTabHome_btnCut');
      if (ribbonTabHomeBtnCut) {
        this.ngZone.run(() => {
          const newRibbonTabHomeBtnCut = ribbonTabHomeBtnCut.cloneNode(true) as HTMLButtonElement;
          newRibbonTabHomeBtnCut.addEventListener('click', this.onRibbonCutButtonClicked.bind(this));
          ribbonTabHomeBtnCut.replaceWith(newRibbonTabHomeBtnCut);
        });
      }

      const ribbonTabHomeBtnServerPaste = document.getElementById('ribbonTabHome_btnServerPaste');
      if (ribbonTabHomeBtnServerPaste) {
        this.ngZone.run(() => {
          ribbonTabHomeBtnServerPaste.addEventListener('click', this.onRibbonPasteButtonClicked.bind(this));
        });
      }
      this.ribbonClipboardPasteComponent.displayCustomPasteButton();

      //this.addCustomMenuItems();
      this.textControlService.showFieldNavigatorSideBar(this.document.isActiveDocument);
      //this.isSideBarOpen will always return false - hiding close button 100% of the time - future feature should query the DOM for if a sidebar is open and toggle the close button respectively
      this.showSideBarCloseButton = this.isSideBarOpen();
      this.textControlService.enableSpellCheck().then((res: boolean) => {
        if (res) {
          this.getTXdocsDictionary();
          this.getFirmDictionary();
        }
      });
      this.textControlService.setLocalClipboard();
      this.catchClickOnSidebarToggleButton();
      this.changeDetectorRef.detectChanges();
      // let find = document.getElementById('ribbonTabHome_btnFind');
      // if (find) {
      //   find.addEventListener('click', this.handleFindButtonClick.bind(this));
      //   alert('registered');
      // } else {
      //   alert('too early');
      // }
    });
  }
  handleFindButtonClick(event: Event): void {
    event.preventDefault(); // Prevent default behavior if necessary
    event.stopImmediatePropagation(); // Stop other event handlers from firing
    this.dialogService.openFindReplaceDialog('find', () => {
      setTimeout(() => {
        TXTextControl.setEditMode(TXTextControl.EditMode.Edit);
        TXTextControl.setActivationState(TXTextControl.ActivationState.Activated);
        TXTextControl.focus();
      }, 100);
    });
    this.changeDetectorRef.detectChanges();
  }

  // Make sure the sidebar toggle button, located in the top right of the ribbon bar,
  // does not show/hide the sidebar but only expands the dropdown menu.
  catchClickOnSidebarToggleButton() {
    document.querySelector('#txTglBtnSideBars > span')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      // trigger click to open dropdown menu
      (document.querySelector('#txTglBtnSideBars .tx-btn-icon-chevron') as any).click();
    });
  }

  onCloseClickSideBar() {
    // Always make sure the TXdocs navigation is open. ??
    this.textControlService.showFieldNavigatorSideBar(false);
  }

  subscribeToChangedObservable() {
    return this.textControlService.watchChangedObservable().pipe(
      takeUntil(this.unsubscribe$),
      tap((e) => {
        this.isDocumentDirty = true;
      })
    );
  }

  subscribeToChangedObservableForAutosave() {
    if (this.autosaveChangedSubscription$) return;

    this.autosaveChangedSubscription$ = this.subscribeToChangedObservable()
      .pipe(
        debounce(() => timer(3000)),
        switchMap(() => {
          if (this.isDocumentDirty || this.isAnswersDirty) {
            // Convert the Promise to Observable using from
            return from(this.textControlService.checkDocumentForAutoSave()).pipe(
              switchMap((res) => {
                if (res) {
                  return this.autosaveService.create(
                    this.document,
                    this.textControlService.txTextSessionID,
                    sessionStorage.getItem('recover_autosave') ?? ''
                  );
                }

                return of(null);
              })
            );
          }
          // It's important to return an Observable here, of(null) ensures that
          return of(null);
        })
      )
      .subscribe();
  }

  subscribeToChangedObservableForICode() {
    if (this.icodeChangedSubscription$) return;

    this.icodeChangedSubscription$ = this.subscribeToChangedObservable()
      .pipe(
        takeUntil(this.unsubscribe$),
        throttle(() => timer(500))
      )
      .subscribe((res) => {
        if (!this.updatingAnswersFromPool && !this.updatingLogic && !this.revertingAnswer) {
          this.checkIfIcodeChanged();
        }
      });
  }

  unsubscribeFromChangedObservableForICode() {
    if (this.icodeChangedSubscription$) {
      this.icodeChangedSubscription$.unsubscribe();
      this.icodeChangedSubscription$ = undefined;
    }
  }

  unsubscribeFromChangedObservableForAutosave() {
    if (this.autosaveChangedSubscription$) {
      this.autosaveChangedSubscription$.unsubscribe();
      this.autosaveChangedSubscription$ = undefined;
    }
  }

  subscribeToFieldDeletedObservable() {
    this.textControlService
      .watchTextFieldDeletedObservable()
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((res) => {
          this.fieldsToRemove.push(res.textField.id);
        }),
        throttle(() => timer(500))
      )
      .subscribe((res) => {
        this.onFieldDeleted(res.textField);
      });
  }

  subscribeToSharedMessage() {
    this.sharedMessage
      .receive()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: SharedMessageModel) => {
        console.log('cmdebug echo service', response);

        switch (response.messageType) {
          case SharedMessageType.Dictionary:
            this.updateFirmDictionary(response.data);
            break;
          default:
            break;
        }
      });
  }

  getAutosavesByStaffId() {
    this.autosaveService
      .getByStaffId(this.authenticationService.getStaffId())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => this.openRecoverDialog(res));
  }

  getAutosavesByCasId() {
    // we don't want to get the autosaves if we are in a tab with a recovered document
    const recoverSessionId = sessionStorage.getItem('recover_autosave');
    if (this.document.casID && this.document.casID !== '' && (recoverSessionId == null || recoverSessionId === '')) {
      this.autosaveService
        .getByCasId(this.document.casID)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((res) => this.openRecoverDialog(res));
    }
  }

  getAutosaveBySessionid(autosaveSessionId: string) {
    this.autosaveService
      .getBySessionId(autosaveSessionId, this.textControlService.txTextSessionID)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loadingService.resetLoadingDocumentIndicator();
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        this.dialogService.info({
          title: 'Document recovered',
          message: `Document ${res.filename} has been recovered.`,
        });
        this.loadDocument(res).then(() => {
          this.isDocumentDirty = true;
          this.autosaveService
            .create(this.document, '', sessionStorage.getItem('recover_autosave') ?? '')
            .subscribe((res) => {
              sessionStorage.setItem('recover_autosave', this.windowService.getSessionIdFromWindow());
            });
        });
      });
  }

  getRefreshBySessionId(resumeSessionId: string, resumeDcmId: string) {
    this.unsubscribeFromChangedObservableForICode();
    this.unsubscribeFromChangedObservableForAutosave();
    this.sessionService
      .resumeSession(resumeSessionId, this.textControlService.txTextSessionID, resumeDcmId)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loadingService.resetLoadingDocumentIndicator();
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        this.loadDocument(res).then((res) => {
          this.checkPendingCaseSessions();
        });
      });
  }

  resumeSessionById(sessionId: string) {
    this.unsubscribeFromChangedObservableForICode();
    this.unsubscribeFromChangedObservableForAutosave();
    this.sessionService
      .resumeSession(sessionId, this.textControlService.txTextSessionID)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loadingService.resetLoadingDocumentIndicator();
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        this.loadDocument(res).then((res) => {
          this.checkPendingCaseSessions();
        });
      });
  }

  openRecoverDialog(autosaves: Array<AutosaveModel>) {
    if (autosaves != null && autosaves.length > 0) {
      const recoveryDialogRef = this.dialogService.openCustomDialog(
        'RecoverDocuments',
        RecoverDocumentsDialogComponent,
        {
          size: 'lg',
          backdrop: 'static',
        }
      );
      recoveryDialogRef.componentInstance.staffID = this.authenticationService.getStaffId();
      recoveryDialogRef.componentInstance.autosaves = autosaves;
      recoveryDialogRef.componentInstance.casID = this.document.casID;
      recoveryDialogRef.componentInstance.casDescription = this.document.casDescription;
      recoveryDialogRef.componentInstance.clientName =
        this.document.ctcLastName && this.document.ctcLastName !== ''
          ? this.document.ctcFirstName + ' ' + this.document.ctcLastName
          : '';
    }
  }

  openDebugDialog() {
    const debugDialogRef = this.dialogService.openCustomDialog('debug', DebugDialogComponent);
    debugDialogRef.componentInstance.firmId = this.authenticationService.getFirmId();
    debugDialogRef.componentInstance.sessionId = this.windowService.getSessionIdFromWindow();
    debugDialogRef.componentInstance.contactId = this.document.ctcID;
    debugDialogRef.componentInstance.caseId = this.document.casID;
    debugDialogRef.componentInstance.documentId = this.document.dcmID;
    debugDialogRef.componentInstance.answerPoolId = this.document.answerPoolId;
    debugDialogRef.componentInstance.appInsightsSessionId = this.authenticationService.getAISession();
    debugDialogRef.componentInstance.selectedField = this.textControlService.formFieldsService.activeSelectedField;
  }

  onFieldDeleted(field: any): any {
    this.textControlService.getInputPosition().then((position: number) => {
      // we don't want to show the dialog when we delete an I code form field on change
      if (this.formFieldToDelete === field.name || this.dialogService.isDialogOpen('replace')) {
        this.formFieldToDelete = '';
        this.textControlService.setFormFieldToDelete('');
      } else {
        this.dialogService.confirm({
          title: 'Delete form field',
          message: 'Are you sure you want to delete this form field?',
          confirmButtonText: 'Delete',
          rejectButtonText: 'Cancel',
          showHeaderCloseButton: false,
          confirmCallback: () => {
            this.loadingService.setLoadingAction(LoadingAction.Updating);
            const promises = [];
            promises.push(this.textControlService.refreshFormFieldsArray(this.fieldsToRemove));
            promises.push(this.textControlService.setInputPosition(position, 0, false));
            Promise.all(promises).then((res) => {
              this.fieldsToRemove = [];
              this.loadingService.resetLoadingDocumentIndicator();
              this.changeDetectorRef.detectChanges();
              if (res) this.textControlService.focus();
            });
          },
          rejectCallback: () => {
            this.fieldsToRemove = [];
            this.textControlService.undoAndFocus();
            this.textControlService.setInputPosition(position, 0, true).then((res) => {
              if (res) this.textControlService.focus();
            });
          },
        });
      }
    });
  }

  handleIcodeValueChange(formField: FormField, name: string, message?: string) {
    if (this.dialogService.isConfirmOpen) return;
    if (this.dialogService.isAnyDialogOpen()) return;

    const defaultMessage = 'This field is automatically updated by TXdocs. Are you sure you want to change the text?';
    this.dialogService.confirm({
      title: 'Form field change',
      message: message ?? defaultMessage,
      confirmButtonText: 'Yes',
      rejectButtonText: 'No',
      showHeaderCloseButton: false,
      confirmCallback: () => {
        this.formFieldToDelete = name;
        this.textControlService.setFormFieldToDelete(this.formFieldToDelete);
        this.textControlService.deleteCurrentFormField();
        //deselect active field after deletion
        this.textControlService.formFieldsService.activeSelectedField = undefined;
        this.textControlService.formFieldsService.currentSelectedFieldId = -1;
      },
      rejectCallback: () => {
        this.formFieldToUndo = name;
        const oldValue = this.documentDataService.getLogicResultByToken(name);
        if (oldValue && oldValue.result) {
          this.revertingAnswer = true;
          this.textControlService.formFieldsService.setFieldTextPromise(formField, oldValue.result).then((res) => {
            this.revertingAnswer = false;
          });
        }
        //end reject callback by setting this.formFieldToUndo = '' - ensures we trigger the dialog correctly per change
        this.textControlService.focus();

        this.formFieldToUndo = '';
        // this.textControlService.undoAndFocus();
      },
    });
  }

  preventFieldEditsAndForceFocusToNavigationPanel() {
    this.textControlService.formFieldsService
      .getCurrentFormFieldObservable()
      .pipe(take(1))
      .subscribe((formField: FormField | undefined) => {
        console.debug('CMDEBUG Handling format/mask value');
        if (formField && formField.details) {
          // we want to avoid this triggering again on undo
          if (formField.details.name === this.formFieldToUndo) {
            this.formFieldToUndo = '';
          } else {
            const obj = this.documentDataService.getObject(formField.details.name);
            if (obj != null) {
              if (obj.mask !== null) {
                this.toastService.show('This field is formatted by TXdocs. Please edit your answer in the side panel', {
                  classname: 'bg-warning',
                  autohide: true,
                });
                const oldValue = this.documentDataService.getAnswerByToken(formField.details.name);

                if (oldValue) {
                  this.revertingAnswer = true;
                  this.textControlService.formFieldsService
                    .setFieldTextPromise(formField, oldValue.answer)
                    .then((res) => {
                      this.revertingAnswer = false;
                    });
                } else {
                  //we still need to focus the input
                  this.formFieldModal?.focusInput();
                }
                this.formFieldModal?.focusInput(); //force focus regardless of any existing answer
              }
            }
          }
        }
      });
  }

  async checkIfIcodeChanged() {
    // check if we are on an icode before doing anything else
    if (
      this.textControlService.formFieldsService.activeSelectedField === undefined ||
      this.textControlService.formFieldsService.activeSelectedField?.details?.name === undefined ||
      this.textControlService.formFieldsService.activeSelectedField?.details?.name.startsWith('I') == false
    ) {
      return;
    }
    await this.textControlService.formFieldsService
      .getCurrentFormFieldPromise()
      .then(async (formField: FormField | undefined) => {
        if (formField && formField.details && formField.details.name.startsWith('I')) {
          console.debug('CMDEBUG Handling Icodeicode value', formField.details.name);

          // we want to avoid this triggering again on undo
          if (formField.details.name === this.formFieldToUndo) {
            this.formFieldToUndo = '';
          } else {
            const obj = this.documentDataService.getObject(formField.details.name);
            if (obj != null) {
              if (obj.qToken != null && obj.qToken !== '') {
                if (obj.hasTransformations || obj.mask !== null) {
                  this.toastService.show(
                    'This field is calculated by TXdocs. Please edit your answer in the side panel',
                    {
                      classname: 'bg-warning',
                      autohide: true,
                    }
                  );
                  const oldValue = this.documentDataService.getLogicResultByToken(formField.details.name);
                  if (oldValue && oldValue.result) {
                    this.revertingAnswer = true;
                    await this.textControlService.formFieldsService
                      .setFieldTextPromise(formField, oldValue.result)
                      .then((res) => {
                        this.revertingAnswer = false;
                        this.formFieldModal?.focusInput();
                      });
                  } else {
                    //we still need to focus the side panel input
                    this.formFieldModal?.focusInput();
                  }
                } else {
                  if (obj.qToken.startsWith('Q')) {
                    const qObj = this.documentDataService.getObject(obj.qToken);
                    if (qObj != null && qObj.qType !== 'E' && qObj.qType !== 'T' && qObj.qType !== 'C') {
                      this.handleIcodeValueChange(formField, formField.details.name);
                    }
                  } else {
                    this.handleIcodeValueChange(formField, formField.details.name);
                  }
                }
              } else {
                this.handleIcodeValueChange(formField, formField.details.name);
              }
            }
          }
        }
      });
  }

  private setDocumentContactAndCase(ctcId: string, casId: string) {
    this.contactService.getContactsAndCases().subscribe({
      next: (res) => {
        if (res && res.contacts) {
          const contact = res.contacts.find((c: ContactModel) => c.ctcID === ctcId);
          if (contact && contact.cases) {
            const cas = contact.cases.find((c: CaseModel) => c.casID === casId);
            if (cas) {
              this.document = {
                ...this.document,
                casID: casId,
                ctcID: ctcId,
                ctcFirstName: contact.firstName,
                ctcLastName: contact.lastName,
                casDescription: cas.description,
                folder: cas.folder,
              };

              this.documentDataService.setCtcId(ctcId);
              this.documentDataService.setCasId(casId);
              this.getContactAddress(ctcId);
            }
          }
          this.isLoading = false;
        }
      },
      error: (err) => {},
    });
  }

  public isSideBarOpen() {
    //query document to see if any sidebar is open and toggle true/false  accordingly
    return false;
  }

  private isInsideCalculatedField(): boolean {
    if (
      !this.textControlService.formFieldsService.activeSelectedField ||
      this.textControlService.isFormFieldFocused ||
      document.activeElement?.id !== 'txHiddenTextField_textView'
    )
      return false;

    const currentFieldObject = this.documentDataService.getObject(
      this.textControlService.formFieldsService.activeSelectedField.details.name
    );

    if (!currentFieldObject) return false;

    return (
      currentFieldObject.token.startsWith('I') && (!currentFieldObject.qToken || currentFieldObject.hasTransformations)
    );
  }

  private storeDocumentData(documentModel: DocumentModel) {
    this.documentDataService.setPraSysId(documentModel.praSysId ?? 0);
    this.documentDataService.setTemplateId(documentModel.templateId ?? 0);
    this.documentDataService.setCasId(documentModel.casID ?? '');
    this.documentDataService.setCtcId(documentModel.ctcID ?? '');
    if (documentModel.objects) {
      this.documentDataService.setObjects(documentModel.objects);
    }
    if (documentModel.answers) {
      this.documentDataService.setAnswers(documentModel.answers);
      this.textControlService.addCaseDictionary();
    }
    if (documentModel.logicResults) {
      this.documentDataService.setLogicResults(documentModel.logicResults);
    }
    if (documentModel.filename) {
      document.title = documentModel.filename;
    }
  }

  private isEditableField(token: string): boolean {
    const obj = this.documentDataService.getObject(token);
    if (obj != null) {
      if (obj.objType === 'Q' && this.isTextField(obj)) {
        return true;
      } else if (obj.qToken && !obj.hasTransformations) {
        const qObj = this.documentDataService.getObject(obj.qToken);
        if (qObj && this.isTextField(qObj)) {
          return true;
        }
      }
    }

    return false;
  }

  private isTextField(obj: ObjectModel) {
    return obj.qType === 'T' || obj.qType === 'E' || obj.qType === 'C';
  }

  private checkShowIntroDialog() {
    if (this.authenticationService.getIsFreeTrial()) {
      this.preferencesService
        .getPreference(this.preferencesService.introPopupShowedPreference, this.authenticationService.getInitials())
        .pipe(take(1))
        .subscribe((res) => {
          if (!res || res != '1') {
            this.dialogService.openCustomDialog('Introduction dialog', IntroductionDialogComponent, {
              size: 'md',
              backdrop: 'static',
              centered: true,
            });
          }
        });
    }
  }

  private getTXdocsDictionary() {
    this.dictionaryService
      .getTXdocsDictionary()
      .pipe(take(1))
      .subscribe((res: DictionaryModel) => {
        if (res && res.words && res.words.length > 0) {
          this.textControlService.addDictionary('TXdocs', res.words);
        }
      });
  }

  private getFirmDictionary() {
    this.dictionaryService
      .getFirmDictionary()
      .pipe(take(1))
      .subscribe((res: DictionaryModel) => {
        let words = [''];
        let count = 0;
        if (res && res.words && res.words.length > 0) {
          words = res.words;
          count = words.length;
        }
        this.textControlService.addDictionary('FirmDictionary', words);
        this.dictionaryService.setFirmDictionaryWordCount(count);
      });
  }

  private saveFirmDictionary() {
    this.textControlService.getFirmDictionary().then((words: string[]) => {
      if (words.length != this.dictionaryService.getFirmDictionaryWordCount()) {
        this.dictionaryService
          .saveFirmDictionary(words)
          .pipe(take(1))
          .subscribe((res: string[]) => {
            if (res && res.length > 0) {
              this.updateFirmDictionary(words);
              this.sharedMessage.post({ messageType: SharedMessageType.Dictionary, data: res });
            }
          });
      }
    });
  }

  private updateFirmDictionary(words: string[]) {
    if (words && words.length > 0) {
      this.textControlService.addDictionary('FirmDictionary', words);
      this.dictionaryService.setFirmDictionaryWordCount(words.length);
    }
  }

  private checkPendingCaseSessions() {
    // Check if we are inside of an iframe. We don't want to open this dialog in an iframe to prevent endless loop
    if (this.document.casID && window.self === window.top) {
      this.sessionService
        .getPendingCaseSessions(this.document.casID)
        .pipe(take(1))
        .subscribe((res) => {
          if (res && res.length > 0) {
            this.hasPendingTakeoverSessions = true;

            const pendingSessionsModalRef = this.dialogService.openCustomDialog(
              'PendingCaseSessionsDialog',
              PendingTakeoverSessionsDialogComponent,
              {
                size: 'xl',
                backdrop: 'static',
                keyboard: false,
                windowClass: 'pending-takeover-modal',
              }
            );

            pendingSessionsModalRef.componentInstance.activeSessions = res;
            pendingSessionsModalRef.componentInstance.casId = this.document.casID;

            pendingSessionsModalRef.result.then((res) => {
              this.hasPendingTakeoverSessions = false;
            });
          }
        });
    }
  }

  private async handleCopyEvent(e: any) {
    this.ngZone.run(async () => {
      if (this.textControlService.clipboardDataTransferInProgress()) {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Copying;
      }

      while (this.textControlService.clipboardDataTransferInProgress()) {
        await new Promise((resolve) => setTimeout(resolve, 100)); // Poll every 100ms
      }

      const frame = await this.textControlService.getCurrentFrame();
      if (frame) {
        // how to get the content of the frame
        console.dir('Current frame: ', frame);
      } else {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Copying;
        this.changeDetectorRef.detectChanges();
        try {
          const text = await this.textControlService.copyCurrentSelectionText();
          const plainTextContent = new Blob([text.plaintext], { type: 'text/plain' });
          const htmlContent = new Blob([text.html], { type: 'text/html' });
          const clipboardItem = new ClipboardItem({ 'text/html': htmlContent, 'text/plain': plainTextContent });
          await navigator.clipboard.write([clipboardItem]);
        } catch (error) {
          console.error(error);
          this.toastService.show('Copy action failed. Please try again.', {
            classname: 'bg-danger',
            autohide: false,
          });
        } finally {
          this.loadingService.loading = false;
          this.loadingService.loadingAction = LoadingAction.None;
          this.changeDetectorRef.detectChanges();
        }
      }
    });
  }

  private async handleCutEvent(e: any) {
    this.ngZone.run(async () => {
      if (this.textControlService.clipboardDataTransferInProgress()) {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Cutting;
      }

      while (this.textControlService.clipboardDataTransferInProgress()) {
        await new Promise((resolve) => setTimeout(resolve, 100)); // Poll every 100ms
      }

      const frame = await this.textControlService.getCurrentFrame();
      if (frame) {
        // how to get the content of the frame
        console.dir('Current frame: ', frame);
      } else {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Cutting;
        this.changeDetectorRef.detectChanges();
        try {
          const text = await this.textControlService.cutCurrentSelectionText();
          const plainTextContent = new Blob([text.plaintext], { type: 'text/plain' });
          const htmlContent = new Blob([text.html], { type: 'text/html' });
          const clipboardItem = new ClipboardItem({ 'text/html': htmlContent, 'text/plain': plainTextContent });
          await navigator.clipboard.write([clipboardItem]);
        } catch (error) {
          console.error(error);
          this.toastService.show('Cut action failed. Please try again.', {
            classname: 'bg-danger',
            autohide: false,
          });
        } finally {
          this.loadingService.loading = false;
          this.loadingService.loadingAction = LoadingAction.None;
          this.changeDetectorRef.detectChanges();
        }
      }
    });
  }

  async onCopyButtonClicked(e: TXTextControl.ContextMenuItemClickEventArgs) {
    e.cancel = true;
    const synced = await this.checkIfSelectionSynced();
    // value is null if there is no selection
    if (synced != null) {
      await this.handleCopyEvent(e);
    }
  }

  async onRibbonCopyButtonClicked(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const synced = await this.checkIfSelectionSynced();
    // value is null if there is no selection
    if (synced != null) {
      await this.handleCopyEvent(e);
    }
  }

  async onCutButtonClicked(e: TXTextControl.ContextMenuItemClickEventArgs) {
    e.cancel = true;
    const synced = await this.checkIfSelectionSynced();
    // value is null if there is no selection
    if (synced != null) {
      await this.handleCutEvent(e);
    }
  }

  async onRibbonCutButtonClicked(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const synced = await this.checkIfSelectionSynced();
    // value is null if there is no selection
    if (synced != null) {
      await this.handleCutEvent(e);
    }
  }

  async onPasteButtonClicked(e: TXTextControl.ContextMenuItemClickEventArgs) {
    e.cancel = true;
    if (
      this.clipboardService.permission() == PermissionStatus.Granted ||
      this.clipboardService.permission() == PermissionStatus.Prompt
    ) {
      await this.textControlService.callAsPromise(TXTextControl.setEditMode, TXTextControl.EditMode.ReadOnly);
      this.ngZone.run(() => {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Pasting;
      });

      try {
        const html = await this.clipboardService.getClipboardItemAsHtml();
        if (html) {
          await this.clipboardService.customPaste(html);
        } else {
          await this.textControlService.callAsPromise(TXTextControl.setEditMode, TXTextControl.EditMode.Edit);
          const clipboardItem = await this.clipboardService.getClipboardItem();

          if (clipboardItem && clipboardItem.types && clipboardItem.types.length > 0) {
            await this.clipboardService.createClipboardPasteEvent(clipboardItem, true);
          } else {
            this.openPasteDialog();
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingService.loading = false;
        this.loadingService.loadingAction = LoadingAction.None;
        await this.textControlService.callAsPromise(TXTextControl.setEditMode, TXTextControl.EditMode.Edit);
        this.textControlService.focus();
      }

      return e;
    } else {
      // this.displayClipboardToast();
      this.openPasteDialog();
    }
    // client clipboard permission is denied, proceed with the default behavior, which is using the server clipboard
    e.cancel = false;
    return e;
  }

  async onRibbonPasteButtonClicked(e: Event) {
    if (e.type === 'keydown' && this.clipboardService.permission() === PermissionStatus.Denied) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (
      this.clipboardService.permission() == PermissionStatus.Granted ||
      this.clipboardService.permission() == PermissionStatus.Prompt
    ) {
      await this.textControlService.callAsPromise(TXTextControl.setEditMode, TXTextControl.EditMode.ReadOnly);
      this.ngZone.run(() => {
        this.loadingService.loading = true;
        this.loadingService.loadingAction = LoadingAction.Pasting;
      });

      try {
        const html = await this.clipboardService.getClipboardItemAsHtml();
        if (html) {
          await this.clipboardService.customPaste(html);
        } else {
          await this.textControlService.callAsPromise(TXTextControl.setEditMode, TXTextControl.EditMode.Edit);
          const clipboardItem = await this.clipboardService.getClipboardItem();

          if (clipboardItem && clipboardItem.types && clipboardItem.types.length > 0) {
            await this.clipboardService.createClipboardPasteEvent(clipboardItem, true);
          } else {
            this.openPasteDialog();
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingService.loading = false;
        this.loadingService.loadingAction = LoadingAction.None;
        await this.textControlService.callAsPromise(TXTextControl.setEditMode, TXTextControl.EditMode.Edit);
        this.textControlService.focus();
      }
    } else {
      // this.displayClipboardToast();
      this.openPasteDialog();
    }
  }

  private async checkIfSelectionSynced(): Promise<boolean | null> {
    // Commenting code out because it looks like we are not getting the expected event anymore
    // The toast is shown every time and can confuse the user
    // The whole selected text is successfully copied despite showing the message
    // Once we verify we don't need the code, we will remove it
    // return this.ngZone.run(async () => {
    //   const currentPosition = await this.textControlService.getSelectionInformationPromise();

    //   if (currentPosition.length === 0) {
    //     // no selection - nothing to sync
    //     return null;
    //   }

    //   const clipboardSelectionPosition = this.textControlService.clipboardDataTransferSelectionPosition();

    //   const synced =
    //     currentPosition.start === clipboardSelectionPosition.start &&
    //     currentPosition.length === clipboardSelectionPosition.length;

    //   if (!synced) {
    //     this.toastService.show(
    //       'The clipboard is not synced yet. The text you copied is different than the selected text',
    //       {
    //         classname: 'bg-warning',
    //         autohide: true,
    //       }
    //     );
    //   }

    //   return synced;
    // });

    return true;
  }

  onPasteDocumentChangedEventHandler() {
    this.ngZone.run(() => {
      document.body.style.setProperty('cursor', 'default', 'important');
      this.textControlService.focus();
      this.textControlService.scrollToPosition(0);
      if (this.dialogService.isDialogOpen('PasteDialog')) {
        this.dialogService.closeDialogs();
      }
      this.textControlService.removeEventListener('changed', this.pasteDocumentChangedEventHandler);
    });
  }

  displayClipboardToast() {
    if (this.hasDisplayedClipboardMessage) {
      return;
    }
    const ctrlKey = this.isUserAgentMacOS() ? '\u2318' : 'Ctrl';
    this.ngZone.run(() => {
      this.toastService.show(
        `Your browser doesn't have access to your clipboard. Please change the browser settings or use the following keyboard shortcuts: \n
        Copy: ${ctrlKey} + C; Cut: ${ctrlKey} + X; Paste: ${ctrlKey} + V.`,
        {
          classname: 'bg-warning',
          autohide: false,
        }
      );
    });
    this.hasDisplayedClipboardMessage = true;
  }

  openPasteDialog() {
    this.ngZone.run(() => {
      this.textControlService.registerEventListener('changed', this.pasteDocumentChangedEventHandler);
      const modalRef = this.dialogService.openCustomDialog('PasteDialog', PasteDialogComponent, {
        size: 'lg',
        centered: true,
      });
      modalRef.componentInstance.isMacOS = this.isUserAgentMacOS();

      modalRef.result.then(
        (html: string) => {
          const dataTransfer = new DataTransfer();
          dataTransfer.setData('text/html', html);
          const pasteEvent = new ClipboardEvent('paste', {
            clipboardData: dataTransfer,
            bubbles: true,
            cancelable: true,
          });

          const targetElement = document.getElementById('txHiddenTextField_textView');
          if (targetElement) {
            targetElement.dispatchEvent(pasteEvent);
          }
        }
        // ).finally(() => {
        //   this.textControlService.removeEventListener('changed', this.pasteDocumentChangedEventHandler);
        // }
      );
    });
  }

  private isUserAgentMacOS() {
    let parser = new UAParser(navigator.userAgent);
    const userAgent = parser.getResult();
    const isMacOS = userAgent.os.name?.toLowerCase() === 'mac os';

    return isMacOS;
  }

  private normalizeQuotes(input: string | null | undefined): string {
    if (input == null) return '';

    const singleQuoteVariants = /[‘’]/g;
    const doubleQuoteVariants = /[“”]/g;

    const result = input
      // .trim()
      .replace(singleQuoteVariants, "'")
      .replace(doubleQuoteVariants, '"');

    return result;
  }

  private areEquivalentPreservingQuoteType(a: string, b: string): boolean {
    const equivalent = this.normalizeQuotes(a) === this.normalizeQuotes(b);
    return equivalent;
  }

  private getContactAddress(ctcId: string) {
    // only used to get contact addresses for a new case document
    this.contactService
      .getContactAddress(ctcId)
      .pipe(take(1))
      .subscribe((res: ContactAddressModel) => {
        if (res) {
          this.document = {
            ...this.document,
            addr1Type: res.addr1Type,
            addr1: res.addr1,
            addr2Type: res.addr2Type,
            addr2: res.addr2,
            billingAddress: res.billingAddress,
          };

          this.documentService.documentWasLoaded(this.document);
        }
      });
  }

  private checkIfTXtextControlLoaded() {
    setTimeout(() => {
      if (!this.txTextControlLoaded) {
        this.sessionService.sendNotificationBeacon({code: 0, reason: 'TXTextControl failed to load within expected time', wasClean: false});
      }
    }, 10000);
  }


}
